import axios from 'axios';
import { ApiResponse, RegisterData, User } from '../types';
import type { AnnexureDefinitionMeta } from '../types';
import { decodeHtmlEntitiesDeep } from '../utils/htmlEntities';
import { normalizeAnnexureDefinitionPayload } from '../utils/annexureQuestionnaireStructure';
import { appendAnnexureApproverExtras } from '../utils/annexureApproverFormData';
import {
  encryptData,
  decryptData,
  isClientApiEncryptionConfigured,
  looksLikeEncryptedApiPayload,
} from '../utils/encryption';

function normalizeAnnexureDefinitionMeta(meta: AnnexureDefinitionMeta): AnnexureDefinitionMeta {
  const definition = normalizeAnnexureDefinitionPayload(meta.definition);
  return definition != null ? { ...meta, definition } : meta;
}

// Vite uses import.meta.env for env variables. Provide sensible default to '/api'.
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

const CLIENT_API_ENCRYPTION_HEADER = 'X-Client-Api-Encryption';

function shouldEncryptClientApi(): boolean {
  return isClientApiEncryptionConfigured();
}

function unwrapEncryptedResponseData(data: unknown): unknown {
  if (!data || typeof data !== 'object' || !('data' in (data as object))) {
    return data;
  }
  const wrapped = (data as { data?: unknown }).data;
  if (!looksLikeEncryptedApiPayload(wrapped)) {
    return data;
  }
  try {
    return decryptData(wrapped);
  } catch {
    return data;
  }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** In-memory CSRF token for cross-origin API (cookie is on API host, not readable via document.cookie). */
let cachedCsrfToken: string | null = null;

function rememberCsrfToken(token: string | null | undefined): void {
  if (token) {
    cachedCsrfToken = token;
  }
}

export function clearCachedCsrfToken(): void {
  cachedCsrfToken = null;
}

function resolveCsrfToken(): string | null {
  return cachedCsrfToken ?? getCookie('csrf_token');
}

async function fetchCsrfToken(): Promise<string | null> {
  const response = await api.get('/auth/csrf');
  rememberCsrfToken(response.data?.csrfToken);
  const fromCookie = getCookie('csrf_token');
  if (!cachedCsrfToken && fromCookie) {
    cachedCsrfToken = fromCookie;
  }
  return resolveCsrfToken();
}

/** Prime CSRF header token after login / on app load (required when frontend and API origins differ). */
export async function ensureCsrfToken(): Promise<void> {
  if (resolveCsrfToken()) {
    return;
  }
  await fetchCsrfToken();
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(String(API_BASE_URL).match(/ngrok/i)
      ? { 'ngrok-skip-browser-warning': 'true' }
      : {}),
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Request interceptor - cookies are sent automatically by browser
// No need to manually add Authorization header when using httpOnly cookies
api.interceptors.request.use(
  config => {
    config.withCredentials = true;
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';

    const method = (config.method ?? 'get').toLowerCase();
    if (!['get', 'head', 'options'].includes(method)) {
      const csrfToken = resolveCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    if (shouldEncryptClientApi()) {
      config.headers[CLIENT_API_ENCRYPTION_HEADER] = '1';
      if (!['get', 'head', 'options'].includes(method)) {
        const payload = config.data;
        const isFormData =
          typeof FormData !== 'undefined' && payload instanceof FormData;
        if (
          payload &&
          typeof payload === 'object' &&
          !isFormData &&
          !Array.isArray(payload) &&
          !('request' in (payload as object))
        ) {
          config.data = { request: encryptData(payload) };
        }
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors, CSRF recovery, and optional client API decrypt
api.interceptors.response.use(
  response => {
    if (
      shouldEncryptClientApi() &&
      response.config.responseType !== 'blob' &&
      response.config.responseType !== 'arraybuffer'
    ) {
      response.data = unwrapEncryptedResponseData(response.data);
    }
    return response;
  },
  async error => {
    if (
      shouldEncryptClientApi() &&
      error.response?.data &&
      error.config?.responseType !== 'blob' &&
      error.config?.responseType !== 'arraybuffer'
    ) {
      error.response.data = unwrapEncryptedResponseData(error.response.data);
    }

    const status = error.response?.status;
    const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
    const lowerErrorMessage = String(errorMessage).toLowerCase();
    const originalConfig = error.config;

    // One retry after refreshing CSRF token (e.g. first request after tab restore).
    if (
      status === 403 &&
      lowerErrorMessage.includes('csrf validation failed') &&
      originalConfig &&
      !originalConfig.__csrfRetried
    ) {
      originalConfig.__csrfRetried = true;
      try {
        const csrfToken = await fetchCsrfToken();
        if (csrfToken) {
          originalConfig.headers = originalConfig.headers ?? {};
          originalConfig.headers['X-CSRF-Token'] = csrfToken;
        }
        return api(originalConfig);
      } catch {
        return Promise.reject(error);
      }
    }

    // Authentication-related error messages that should trigger logout
    const authErrorMessages = [
      'invalid token',
      'invalid or expired token',
    ];

    // Check if it's an authentication error (401 or 403 with auth-related message)
    const isAuthError = status === 401 ||
      (status === 403 && authErrorMessages.some(msg => lowerErrorMessage.includes(msg)));

    if (isAuthError) {
      // Don't redirect if we're already on the login page or reset password pages (to prevent refresh loop)
      const currentPath = window.location.pathname;
      const isPublicRoute =
        currentPath === '/login' ||
        currentPath.startsWith('/login') ||
        currentPath.startsWith('/forget-password/') ||
        currentPath.startsWith('/forgot-password/') ||
        currentPath === '/forgot-password' ||
        currentPath === '/register';

      if (!isPublicRoute) {
        // Clear any remaining sessionStorage items (for backward compatibility)
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('permissions');
        sessionStorage.removeItem('mfa_session_token');
        sessionStorage.removeItem('selectedSystem');
        // Cookie will be cleared by backend on logout
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

function upgradeToHttpsIfNeeded(url: string): string {
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    url.startsWith('http://')
  ) {
    return `https://${url.slice('http://'.length)}`;
  }
  return url;
}

/** API host origin for joining relative `/uploads/...` paths (avoids broken `/u/uploads` joins). */
function getApiOriginForUploads(): string | null {
  const apiBase = String(API_BASE_URL).trim().replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(apiBase)) return null;
  try {
    return new URL(apiBase).origin;
  } catch {
    return null;
  }
}

/** Prefer API origin for credentialed /uploads fetches; fall back to the page origin (dev proxy). */
function resolveUploadsOrigin(): string | null {
  return getApiOriginForUploads() ?? (typeof window !== 'undefined' ? window.location.origin : null);
}

export function resolveUploadUrl(url: string | null | undefined): string {
  const raw = String(url ?? '')
    .trim()
    .replace(/&amp;(#x2F;|#47;|sol;)/gi, '&$1')
    .replace(/&#x2F;|&#47;|&sol;/gi, '/');
  if (!raw) return '#';
  if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      if (parsed.pathname.startsWith('/uploads/')) {
        const origin = resolveUploadsOrigin();
        if (origin) {
          return upgradeToHttpsIfNeeded(`${origin}${parsed.pathname}${parsed.search}`);
        }
      }
    } catch {
      /* use raw URL below */
    }
    return upgradeToHttpsIfNeeded(raw);
  }

  if (raw.startsWith('//')) {
    const scheme =
      typeof window !== 'undefined' && window.location.protocol === 'https:'
        ? 'https:'
        : 'http:';
    return upgradeToHttpsIfNeeded(`${scheme}${raw}`);
  }

  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  if (!normalizedPath.startsWith('/uploads/')) return normalizedPath;

  const origin = resolveUploadsOrigin();
  if (!origin) return normalizedPath;
  return upgradeToHttpsIfNeeded(`${origin}${normalizedPath}`);
}

// Auth API — plain JSON over HTTPS (Phase 2 Wave 1)
export const authAPI = {
  login: async (payload: {
    email: string;
    password: string;
  }): Promise<
    ApiResponse<{ user: User; token: string; requiresMFA?: boolean; mfaStepToken?: string }>
  > => {
    const response = await api.post('/auth/login', payload);
    return response.data;
  },

  // TOTP initiation — requires mfaStepToken from successful password login
  startEmailLogin: async (
    email: string,
    mfaStepToken: string
  ): Promise<ApiResponse<{ requiresTotp: boolean; alreadySetup: boolean; qrCode?: string }>> => {
    const response = await api.post('/auth/login-email', { email, mfaStepToken });
    return response.data;
  },

  // New TOTP verification endpoint
  verifyTotp: async (
    email: string,
    token: string,
    mfaStepToken: string
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await api.post('/auth/verify-totp', { email, token, mfaStepToken });
    return response.data;
  },

  // Admin/Support: reset or generate QR for a user by email
  resetMfaQr: async (email: string): Promise<ApiResponse<{ qrCode: string }>> => {
    await ensureCsrfToken();
    const response = await api.post('/auth/reset-qr', { email });
    return response.data;
  },

  register: async (userData: RegisterData): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    clearCachedCsrfToken();
    return response.data;
  },

  verify: async (): Promise<ApiResponse<{ user: User }>> => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  requestOTP: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/request-otp', { email });
    return response.data;
  },

  verifyOTP: async (email: string, otp: string): Promise<ApiResponse<{ token: string }>> => {
    const response = await api.post('/auth/verify-otp', { email, otp });
    return response.data;
  },

  resetPassword: async (
    email: string,
    newPassword: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/reset-password', { email, newPassword });
    return response.data;
  },
  forgetPassword: async (
    resetToken: string,
    newPassword: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/forget-password', { resetToken, newPassword });
    return response.data;
  },

  checkPasswordReset: async (
    resetToken: string
  ): Promise<ApiResponse<{ email: string; password_reset: boolean }>> => {
    const response = await api.post('/users/check-password-reset', { resetToken });
    return response.data;
  },
};

// Dashboard API — plain JSON (Phase 2 Wave 3)
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },

  getVendorPerformance: async () => {
    const response = await api.get('/dashboard/vendor-performance');
    return response.data;
  },

  getVendorAnalytics: async (params?: Record<string, any>) => {
    const response = await api.get('/dashboard/vendor-analytics', { params });
    return response.data;
  },

  getVendorAnalyticsDaily: async (params: { date: string } & Record<string, any>) => {
    const response = await api.get('/dashboard/vendor-analytics/daily', { params });
    return response.data;
  },

  getVendorMonthDaily: async (params?: Record<string, any>) => {
    const response = await api.get('/dashboard/vendor-analytics/vendor-month-daily', { params });
    return response.data;
  },
};

// Vendors API — plain JSON (Phase 2 Wave 3)
export const vendorsAPI = {
  getAll: async (options?: {
    page?: number;
    limit?: number;
    search?: string;
    order?: 'asc' | 'desc';
  }) => {
    const params: any = {};

    if (options) {
      if (options.page !== undefined) params.page = options.page;
      if (options.limit !== undefined) params.limit = options.limit;
      if (options.search && options.search.trim()) params.search = options.search.trim();
      if (options.order) params.order = options.order;
    }

    const response = await api.get('/vendors', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/vendors/${id}`);
    return response.data;
  },

  getCreditAccounts: async (vendorId: number) => {
    const response = await api.get(`/vendors/${vendorId}/credit-accounts`);
    return response.data;
  },

  create: async (vendorData: any) => {
    const response = await api.post('/vendors', vendorData);
    return response.data;
  },

  update: async (id: number, vendorData: any) => {
    const response = await api.put(`/vendors/${id}`, vendorData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/vendors/${id}`);
    return response.data;
  },

  exportVendors: async (options?: {
    search?: string;
  }) => {
    const params: any = {};

    if (options) {
      if (options.search && options.search.trim()) params.search = options.search.trim();
    }

    const response = await api.get('/vendors/export', { params });
    return response.data;
  },

  bulkImport: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/vendors/bulk-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  dropdown: async (id: any) => {
    const response = await api.get('/vendors/by-department', {
      params: { department_id: id },
    });
    return response.data;
  },
  allVendorsDropdown: async () => {
    const response = await api.get('/vendors/dropdown');
    return response.data;
  },
};

// Vendors New API — plain JSON (Phase 2 Wave 3)
export const vendorsNewAPI = {
  getAll: async (options?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    vendor_type?: 'Individual' | 'Company';
  }) => {
    const params: any = {};

    if (options) {
      if (options.page !== undefined) params.page = options.page;
      if (options.limit !== undefined) params.limit = options.limit;
      if (options.search && options.search.trim()) params.search = options.search.trim();
      if (options.status && options.status !== 'all') params.status = options.status;
      if (options.vendor_type) params.vendor_type = options.vendor_type;
    }

    const response = await api.get('/vendors-new', { params });
    return response.data;
  },

  exportVendors: async (options?: {
    search?: string;
    status?: string;
    vendor_type?: 'Individual' | 'Company';
  }) => {
    const params: any = {};

    if (options) {
      if (options.search && options.search.trim()) params.search = options.search.trim();
      if (options.status && options.status !== 'all') params.status = options.status;
      if (options.vendor_type) params.vendor_type = options.vendor_type;
    }

    const response = await api.get('/vendors-new/export', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/vendors-new/${id}`);
    return response.data;
  },

  getAuditTrail: async (id: number) => {
    const response = await api.get(`/vendors-new/${id}/audit-trail`);
    return response.data;
  },

  create: async (vendorData: any) => {
    const response = await api.post('/vendors-new', vendorData);
    return response.data;
  },

  update: async (id: number, vendorData: any) => {
    const response = await api.put(`/vendors-new/${id}`, vendorData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/vendors-new/${id}`);
    return response.data;
  },

  // Document Management
  uploadDocument: async (
    vendorId: number,
    documentData: {
      document_type: string;
      document_name: string;
      description?: string;
      expiry_date?: string;
    },
    file: File
  ) => {
    const formData = new FormData();
    formData.append('document_type', documentData.document_type);
    formData.append('document_name', documentData.document_name);
    if (documentData.description) formData.append('description', documentData.description);
    if (documentData.expiry_date) formData.append('expiry_date', documentData.expiry_date);
    formData.append('document_file', file);

    const response = await api.post(`/vendors-new/${vendorId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getDocuments: async (
    vendorId: number,
    filters?: {
      status?: string;
      document_type?: string;
    }
  ) => {
    const params: any = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.document_type) params.document_type = filters.document_type;

    const response = await api.get(`/vendors-new/${vendorId}/documents`, { params });
    return response.data;
  },
};

// Vendor Bank Details API
// Vendor bank API — plain JSON (Phase 2 Wave 2)
export const vendorBankAPI = {
  getAll: async (vendorId: number) => {
    const response = await api.get(`/vendor-bank/vendor/${vendorId}`);
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/vendor-bank/${id}`);
    return response.data;
  },

  create: async (bankData: {
    vendor_id: number;
    bank_name: string;
    beneficiary_name: string;
    account_number: string;
    sort_code?: string;
    iban_no?: string;
    bic?: string;
  }) => {
    const response = await api.post('/vendor-bank', bankData);
    return response.data;
  },

  update: async (
    id: number,
    bankData: {
      bank_name?: string;
      beneficiary_name?: string;
      account_number?: string;
      sort_code?: string;
      iban_no?: string;
      bic?: string;
    }
  ) => {
    const response = await api.put(`/vendor-bank/${id}`, bankData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/vendor-bank/${id}`);
    return response.data;
  },
};

// Vendor Invoices API — plain JSON (Phase 2 Wave 3)
export const vendorInvoicesAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    my_tray?: boolean;
  }) => {
    // Clean params: remove undefined to avoid sending empty query keys
    const cleanParams: any = {};
    if (params) {
      if (params.page !== undefined) cleanParams.page = params.page;
      if (params.limit !== undefined) cleanParams.limit = params.limit;
      if (params.search !== undefined && params.search !== '') cleanParams.search = params.search;
      if (params.status !== undefined && params.status !== '' && params.status !== 'all')
        cleanParams.status = params.status;
      if (params.my_tray === true) cleanParams.my_tray = '1';
    }
    const response = await api.get('/vendor-invoices', { params: cleanParams });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/vendor-invoices/${id}`);
    return response.data;
  },

  upload: async (formData: FormData) => {
    const response = await api.post('/vendor-invoices', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  update: async (id: number, formData: FormData) => {
    const response = await api.put(`/vendor-invoices/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateStatus: async (
    id: number,
    status?: string,
    notes?: string,
    bankId?: string,
    financeStatus?: string,
    financeAccountNo?: string,
    financeAccountName?: string
  ) => {
    const response = await api.put(`/vendor-invoices/${id}/status`, {
      status,
      notes,
      bank_id: bankId,
      finance_status: financeStatus,
      finance_account_no: financeAccountNo,
      finance_account_name: financeAccountName,
    });
    return response.data;
  },

  markPaid: async (
    id: number,
    paidAmount: number,
    paymentDate?: string,
    notes?: string,
    referenceNumber?: string
  ) => {
    const response = await api.post(`/vendor-invoices/${id}/mark-paid`, {
      paid_amount: paidAmount,
      payment_date: paymentDate,
      notes,
      reference_number: referenceNumber,
    });
    return response.data;
  },

  // Mark paid with file attachment (multipart)
  markPaidWithAttachment: async (
    id: number,
    params: {
      paid_amount: number;
      payment_date?: string;
      notes?: string;
      reference_number?: string;
      payment_attachement: File;
    }
  ) => {
    const formData = new FormData();
    formData.append('paid_amount', String(params.paid_amount));
    if (params.payment_date) formData.append('payment_date', params.payment_date);
    if (params.notes) formData.append('notes', params.notes);
    if (params.reference_number) formData.append('reference_number', params.reference_number);
    // Required field name per spec
    formData.append('payment_attachement', params.payment_attachement);

    const response = await api.post(`/vendor-invoices/${id}/mark-paid`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  download: async (id: number) => {
    const response = await api.get(`/vendor-invoices/${id}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  downloadAdditionalAttachment: async (
    invoiceId: number,
    ref: { filename?: string; filepath?: string; index?: number }
  ) => {
    const response = await api.get(
      `/vendor-invoices/${invoiceId}/additional-attachments/download`,
      {
        params: {
          filename: ref.filename,
          filepath: ref.filepath,
          index: ref.index,
        },
        responseType: 'blob',
      }
    );
    return response;
  },

  // Download approval file
  downloadApprovalFile: async (invoiceId: number, fileId: number) => {
    const response = await api.get(
      `/vendor-invoices/${invoiceId}/approval-files/${fileId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response;
  },

  // New API per spec: DELETE /api/vendor-invoices/:id/additional-attachments?filepath=...
  deleteAdditionalAttachment: async (invoiceId: number, filepath: string) => {
    const response = await api.delete(`/vendor-invoices/${invoiceId}/additional-attachments`, {
      params: { filepath },
    });
    return response.data;
  },

  delete: async (id: number, deletion_reason?: string) => {
    const response = await api.delete(`/vendor-invoices/${id}`, {
      data: { deletion_reason },
    });
    return response.data;
  },

  invoiceApproverDropdown: async () => {
    const response = await api.get('/users/invoice-approvers');
    return response.data;
  },

  invoiceInitiatorDropdown: async () => {
    const response = await api.get('/users/invoice-initiators');
    return response.data;
  },

  paymentApproverDropdown: async () => {
    const response = await api.get('/users/payment-approvers');
    return response.data;
  },

  getAuditTrail: async (id: number) => {
    const response = await api.get(`/vendor-invoices/${id}/audit-trail`);
    return response.data;
  },

  getVendorMonthlyStats: async (vendorId: number) => {
    const response = await api.get(`/vendor-invoices/vendor/${vendorId}/monthly-stats`);
    return response.data;
  },

  // Approver status update per-level (1..3)
  setApproverStatus: async (
    id: number,
    level: 1 | 2 | 3,
    status: 'approved' | 'rejected',
    comments?: string
  ) => {
    const response = await api.post(`/vendor-invoices/${id}/approver-status`, {
      level,
      status,
      comments,
    });
    return response.data;
  },

  // Stats for vendor invoices
  getStats: async () => {
    const response = await api.get('/vendor-invoices/stats');
    return response.data;
  },

  // Check if invoice number is duplicate for a vendor
  checkDuplicateInvoice: async (vendorId: number, invoiceNumber: string) => {
    const response = await api.get('/vendor-invoices/check-duplicate', {
      params: { vendor_id: vendorId, invoice_number: invoiceNumber },
    });
    return response.data;
  },

  // Check if registration number matches vendor's registered number
  checkRegistrationNumber: async (vendorId: number, registrationNumber: string) => {
    const response = await api.get('/vendor-invoices/check-registration', {
      params: { vendor_id: vendorId, registration_number: registrationNumber },
    });
    return response.data;
  },

  // Approve or reject vendor invoice (dynamic approvers flow)
  approve: async (
    id: number,
    action: 'approved' | 'rejected',
    notes?: string,
    approvalFile?: File
  ) => {
    // Always use FormData (file is optional)
    const formData = new FormData();
    formData.append('action', action);
    if (notes) formData.append('notes', notes);
    if (approvalFile) formData.append('approval_file', approvalFile);

    const response = await api.post(`/vendor-invoices/${id}/approve`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Update status to work in progress when payment processor downloads digital voucher
  updateStatusToWorkInProgress: async (id: number) => {
    const response = await api.post(`/vendor-invoices/${id}/update-to-wip`);
    return response.data;
  },
};

// MFA API
export const mfaAPI = {
  setup: () => api.post('/mfa/setup'),
  verify: (data: { token?: string; backupCode?: string }) => api.post('/mfa/verify', data),
  disable: (data: { password: string }) => api.post('/mfa/disable', data),
  regenerateBackupCodes: (data: { password: string }) =>
    api.post('/mfa/backup-codes/regenerate', data),
  getStatus: () => api.get('/mfa/status'),
  getSessions: () => api.get('/mfa/sessions'),
  invalidateSession: (sessionId: string) => api.delete(`/mfa/sessions/${sessionId}`),
  validateSession: (sessionToken: string) => api.post('/mfa/validate-session', { sessionToken }),
};

// User Management API — plain JSON (Phase 2 Wave 2)
export const usersAPI = {
  getPermissions: async () => {
    const response = await api.get('/users/permissions');
    return response.data;
  },

  getRoles: async () => {
    const response = await api.get('/users/roles');
    return response.data;
  },

  getAll: async (options?: {
    page?: number;
    limit?: number;
    search?: string;
    platform?: 'ims' | 'vms';
  }) => {
    const params = options
      ? {
          page: options.page,
          limit: options.limit,
          search: options.search,
          platform: options.platform,
        }
      : {};
    const response = await api.get('/users', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (userData: any) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  update: async (id: number, userData: any) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  delete: async (id: number, options?: { platform?: 'ims' | 'vms' }) => {
    const params = options?.platform ? { platform: options.platform } : {};
    const response = await api.delete(`/users/${id}`, { params });
    return response.data;
  },

  updateRole: async (id: number, role: string) => {
    const response = await api.put(`/users/${id}/role`, { role });
    return response.data;
  },

  // Bulk upload users via Excel/CSV (platform: ims = Permission only; vms = VMS roles only)
  bulkUpload: async (file: File, platform: 'ims' | 'vms' = 'ims') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('platform', platform);

    const response = await api.post('/users/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Check if employee ID exists
  checkEmployeeId: async (
    employeeId: string,
    options?: { email?: string; excludeUserId?: number }
  ): Promise<ApiResponse<{ employee_id: string; exists: boolean }>> => {
    const response = await api.post('/users/check-employee-id', {
      employee_id: employeeId,
      ...(options?.email ? { email: options.email } : {}),
      ...(options?.excludeUserId != null ? { exclude_user_id: options.excludeUserId } : {}),
    });
    return response.data;
  },

  // Resend onboarding (account creation) email to user
  resendAccountEmail: async (userId: number) => {
    const response = await api.post(`/users/${userId}/resend-account-email`);
    return response.data;
  },
};

// Departments API — plain JSON (Phase 2 Wave 2)
export const departmentsAPI = {
  getAll: async (options?: { page?: number; limit?: number; search?: string }) => {
    const params = options
      ? {
          page: options.page,
          limit: options.limit,
          search: options.search,
        }
      : {};
    const response = await api.get('/departments', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/departments/${id}`);
    return response.data;
  },

  create: async (departmentData: any) => {
    const response = await api.post('/departments', departmentData);
    return response.data;
  },

  update: async (id: number, departmentData: any) => {
    const response = await api.put(`/departments/${id}`, departmentData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/departments/${id}`);
    return response.data;
  },

  dropDown: async () => {
    const response = await api.get('/departments/dropdown');
    return response.data;
  },
};

/** Shared IMS/VMS department catalog (`departments` table). VMS admin routes use the same data. */
export const vmsDepartmentsAPI = {
  getAll: async (options?: { page?: number; limit?: number; search?: string }) => {
    const params = options
      ? { page: options.page, limit: options.limit, search: options.search }
      : {};
    const response = await api.get('/vms-departments', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/vms-departments/${id}`);
    return response.data;
  },
  create: async (departmentData: Record<string, unknown>) => {
    const response = await api.post('/vms-departments', departmentData);
    return response.data;
  },
  update: async (id: number, departmentData: Record<string, unknown>) => {
    const response = await api.put(`/vms-departments/${id}`, departmentData);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/vms-departments/${id}`);
    return response.data;
  },
  dropDown: async () => {
    const response = await api.get('/vms-departments/dropdown');
    return response.data;
  },
  bulkUpload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/vms-departments/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as {
      success?: boolean;
      message?: string;
      data?: {
        created_count?: number;
        skipped_count?: number;
        skipped?: Array<{ row: number; department: string; reason: string }>;
      };
    };
  },
};

// Currencies API — plain JSON (Phase 2 Wave 2)
export const currenciesAPI = {
  getAll: async (options?: { page?: number; limit?: number; search?: string }) => {
    const params: Record<string, unknown> = {};
    if (options) {
      if (options.page !== undefined) {
        params.page = options.page;
      }
      if (options.limit !== undefined) {
        params.limit = options.limit;
      }
      if (options.search !== undefined && options.search !== '') {
        params.search = options.search;
      }
    }
    const response = await api.get('/currencies', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/currencies/${id}`);
    return response.data;
  },

  create: async (currencyData: any) => {
    const response = await api.post('/currencies', currencyData);
    return response.data;
  },

  update: async (id: number, currencyData: any) => {
    const response = await api.put(`/currencies/${id}`, currencyData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/currencies/${id}`);
    return response.data;
  },

  dropDown: async () => {
    const response = await api.get('/currencies/dropdown');
    return response.data;
  },
};

// Notifications API — plain JSON (Phase 2 Wave 3)
export const notificationsAPI = {
  getAll: async (options?: {
    is_read?: boolean;
    type?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params: any = {};
    if (options) {
      if (options.is_read !== undefined) params.is_read = options.is_read;
      if (options.type) params.type = options.type;
      if (options.limit) params.limit = options.limit;
      if (options.offset) params.offset = options.offset;
    }
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  },

  markAsRead: async (id: number) => {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.patch('/notifications/read-all');
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },
};

// Debit Account Expense API (admin only) — plain JSON (Phase 2 Wave 2)
export const debitAccountExpensesAPI = {
  getAll: async (options?: { page?: number; limit?: number; search?: string }) => {
    const params: Record<string, unknown> = {};
    if (options) {
      if (options.page !== undefined) params.page = options.page;
      if (options.limit !== undefined) params.limit = options.limit;
      if (options.search !== undefined && options.search !== '') params.search = options.search;
    }
    const response = await api.get('/debit-account-expenses', { params });
    return response.data;
  },
  /** Returns all debit account expenses in one call (for dropdowns). No pagination. */
  dropdown: async () => {
    const response = await api.get('/debit-account-expenses/dropdown');
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/debit-account-expenses/${id}`);
    return response.data;
  },
  create: async (data: { account_number?: string; account_name?: string }) => {
    const response = await api.post('/debit-account-expenses', data);
    return response.data;
  },
  update: async (id: number, data: { account_number?: string; account_name?: string }) => {
    const response = await api.put(`/debit-account-expenses/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/debit-account-expenses/${id}`);
    return response.data;
  },
  bulkUpload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/debit-account-expenses/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// bank API
export const bankAPI = {
  create: async (bankData: any) => {
    const response = await api.post('/banks', bankData);
    return response.data;
  },
  getAll: async (vendor_id: number) => {
    const response = await api.get(`/banks/${vendor_id}`);
    return response.data;
  },
};

// Financial Powers API — plain JSON (Phase 2 Wave 3)
export const financialPowersAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    department_id?: number;
    vendor_id?: number;
    my_tray?: boolean;
  }) => {
    const cleanParams: any = {};
    if (params) {
      if (params.page !== undefined) cleanParams.page = params.page;
      if (params.limit !== undefined) cleanParams.limit = params.limit;
      if (params.search !== undefined && params.search !== '') cleanParams.search = params.search;
      if (params.status !== undefined && params.status !== '' && params.status !== 'all')
        cleanParams.status = params.status;
      if (params.department_id !== undefined) cleanParams.department_id = params.department_id;
      if (params.vendor_id !== undefined) cleanParams.vendor_id = params.vendor_id;
      if (params.my_tray === true) cleanParams.my_tray = '1';
    }
    const response = await api.get('/financial-powers', { params: cleanParams });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/financial-powers/${id}`);
    return response.data;
  },

  download: async (id: number) => {
    const response = await api.get(`/financial-powers/${id}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  downloadAdditionalAttachment: async (
    id: number,
    params: { filename?: string; filepath?: string; index?: number }
  ) => {
    const response = await api.get(`/financial-powers/${id}/additional-attachments/download`, {
      params,
      responseType: 'blob',
    });
    return response;
  },

  create: async (formData: FormData) => {
    const response = await api.post('/financial-powers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  update: async (id: number, formData: FormData) => {
    const response = await api.put(`/financial-powers/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  approve: async (
    id: number,
    status: 'approved' | 'rejected',
    notes?: string,
    reference_name?: string
  ) => {
    const payload: any = {
      status,
    };
    if (notes) payload.notes = notes;
    if (reference_name) payload.reference_name = reference_name;

    const response = await api.post(`/financial-powers/${id}/approve`, payload);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/financial-powers/${id}`);
    return response.data;
  },

  getAuditTrail: async (id: number) => {
    const response = await api.get(`/financial-powers/${id}/audit-trail`);
    return response.data;
  },

  getByReference: async (referenceNumber: string) => {
    const response = await api.get(`/financial-powers/by-reference/${referenceNumber}`);
    return response.data;
  },

  dropdown: async () => {
    const response = await api.get('/financial-powers/dropdown');
    return response.data;
  },
};

// Reports API — plain JSON (Phase 2 Wave 3)
export const reportsAPI = {
  getVendorInvoicesDetailed: async (params?: {
    vendor_id?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
    department_id?: number;
    amount?: number;
    month?: number;
    page?: number;
    limit?: number;
  }) => {
    const cleanParams: any = {};
    if (params) {
      if (params.vendor_id !== undefined) cleanParams.vendor_id = params.vendor_id;
      if (params.status !== undefined && params.status !== '') cleanParams.status = params.status;
      if (params.start_date !== undefined && params.start_date !== '')
        cleanParams.start_date = params.start_date;
      if (params.end_date !== undefined && params.end_date !== '')
        cleanParams.end_date = params.end_date;
      if (params.department_id !== undefined && params.department_id > 0)
        cleanParams.department_id = params.department_id;
      if (params.amount !== undefined && params.amount > 0) cleanParams.amount = params.amount;
      if (params.month !== undefined && params.month >= 1 && params.month <= 12)
        cleanParams.month = params.month;
      if (params.page !== undefined) cleanParams.page = params.page;
      if (params.limit !== undefined) cleanParams.limit = params.limit;
    }
    const response = await api.get('/reports/vendor-invoices/detailed', { params: cleanParams });
    return response.data;
  },

  getVendorInvoiceDetailedById: async (id: number) => {
    const response = await api.get(`/reports/vendor-invoices/${id}/detailed`);
    return response.data;
  },

  exportVendorInvoices: async (params?: {
    vendor_id?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
    department_id?: number;
    amount?: number;
    month?: number;
  }) => {
    const cleanParams: any = {};
    if (params) {
      if (params.vendor_id !== undefined) cleanParams.vendor_id = params.vendor_id;
      if (params.status !== undefined && params.status !== '' && params.status !== 'all')
        cleanParams.status = params.status;
      if (params.start_date !== undefined && params.start_date !== '')
        cleanParams.start_date = params.start_date;
      if (params.end_date !== undefined && params.end_date !== '')
        cleanParams.end_date = params.end_date;
      if (params.department_id !== undefined && params.department_id > 0)
        cleanParams.department_id = params.department_id;
      if (params.amount !== undefined && params.amount > 0) cleanParams.amount = params.amount;
      if (params.month !== undefined && params.month >= 1 && params.month <= 12)
        cleanParams.month = params.month;
    }
    const response = await api.get('/reports/vendor-invoices/export', { params: cleanParams });
    return response.data;
  },
};

// Annexure API – fetch definition and optional existing response
export const annexureAPI = {
  list: async (hub?: 'annexures' | 'dpia' | 'contract'): Promise<import('../types').AnnexureDefinitionListItem[]> => {
    const response = await api.get('/annexures', {
      params: hub ? { hub } : undefined,
    });
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
  },
  getByCode: async (
    code: string,
    params?: {
      service_initiation_id?: number;
      vendor_id?: number;
      vendor_initiation_id?: number;
      vms_registered_vendor_id?: number;
      ongoing_batch_id?: number;
    }
  ): Promise<{ definition: import('../types').AnnexureDefinitionMeta; response: Record<string, unknown> | null }> => {
    const response = await api.get(`/annexures/${encodeURIComponent(code)}`, { params });
    const data = response.data?.data ?? response.data;
    const definition = normalizeAnnexureDefinitionMeta(data.definition);
    const rawResponse = data.response ?? null;
    const responseDecoded =
      rawResponse != null && typeof rawResponse === 'object' && !Array.isArray(rawResponse)
        ? decodeHtmlEntitiesDeep(rawResponse as Record<string, unknown>)
        : rawResponse;
    return {
      definition,
      response: responseDecoded,
    };
  },
  create: async (payload: {
    code: string;
    title: string;
    scope: string;
    annexure_type: string;
    definition: import('../types').AnnexureDefinitionPayload;
  }): Promise<import('../types').AnnexureDefinitionMeta> => {
    const response = await api.post('/annexures', payload);
    const data = response.data?.data ?? response.data;
    return normalizeAnnexureDefinitionMeta(data);
  },
  update: async (
    code: string,
    payload: {
      title: string;
      scope: string;
      annexure_type: string;
      definition: import('../types').AnnexureDefinitionPayload;
    }
  ): Promise<import('../types').AnnexureDefinitionMeta> => {
    const response = await api.put(`/annexures/${encodeURIComponent(code)}`, payload);
    const data = response.data?.data ?? response.data;
    return normalizeAnnexureDefinitionMeta(data);
  },
  delete: async (code: string): Promise<void> => {
    await api.delete(`/annexures/${encodeURIComponent(code)}`);
  },
};

export const dpiaAPI = {
  list: async (): Promise<import('../types').DpiaDefinitionListItem[]> => {
    const response = await api.get('/dpia');
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
  },
  getByCode: async (code: string): Promise<{ definition: import('../types').DpiaDefinitionMeta; response: Record<string, unknown> | null }> => {
    const response = await api.get(`/dpia/${encodeURIComponent(code)}`);
    const data = response.data?.data ?? response.data;
    return {
      definition: data.definition,
      response: data.response ?? null,
    };
  },
  create: async (payload: {
    code: string;
    title: string;
    scope: string;
    dpia_type: string;
    definition: import('../types').DpiaDefinitionPayload;
  }): Promise<import('../types').DpiaDefinitionMeta> => {
    const response = await api.post('/dpia', payload);
    const data = response.data?.data ?? response.data;
    return data;
  },
  update: async (
    code: string,
    payload: {
      title: string;
      scope: string;
      dpia_type: string;
      definition: import('../types').DpiaDefinitionPayload;
    }
  ): Promise<import('../types').DpiaDefinitionMeta> => {
    const response = await api.put(`/dpia/${encodeURIComponent(code)}`, payload);
    const data = response.data?.data ?? response.data;
    return data;
  },
  delete: async (code: string): Promise<void> => {
    await api.delete(`/dpia/${encodeURIComponent(code)}`);
  },
};

export const contractAPI = {
  list: async (): Promise<import('../types').ContractDefinitionListItem[]> => {
    const response = await api.get('/contracts');
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
  },
  getByCode: async (
    code: string
  ): Promise<{ definition: import('../types').ContractDefinitionMeta; response: Record<string, unknown> | null }> => {
    const response = await api.get(`/contracts/${encodeURIComponent(code)}`);
    const data = response.data?.data ?? response.data;
    return {
      definition: data.definition,
      response: data.response ?? null,
    };
  },
  create: async (payload: {
    code: string;
    title: string;
    scope: string;
    contract_type: string;
    definition: import('../types').ContractDefinitionPayload;
  }): Promise<import('../types').ContractDefinitionMeta> => {
    const response = await api.post('/contracts', payload);
    const data = response.data?.data ?? response.data;
    return data;
  },
  update: async (
    code: string,
    payload: {
      title: string;
      scope: string;
      contract_type: string;
      definition: import('../types').ContractDefinitionPayload;
    }
  ): Promise<import('../types').ContractDefinitionMeta> => {
    const response = await api.put(`/contracts/${encodeURIComponent(code)}`, payload);
    const data = response.data?.data ?? response.data;
    return data;
  },
  delete: async (code: string): Promise<void> => {
    await api.delete(`/contracts/${encodeURIComponent(code)}`);
  },
};

export const rfpAPI = {
  stakeholdersDropdown: async (params?: { department_id?: number }) => {
    const response = await api.get('/users/stakeholders', { params });
    return response.data;
  },
  businessHeadsDropdown: async (params?: { department_id?: number }) => {
    const response = await api.get('/users/business-heads', { params });
    return response.data;
  },
  departmentRmsDropdown: async (params?: { department_id?: number }) => {
    const response = await api.get('/users/department-rms', { params });
    return response.data;
  },
  vmsLegalTeamDropdown: async (params?: { department_id?: number }) => {
    const response = await api.get('/users/vms-legal', { params });
    return response.data;
  },
  vmsDpoTeamDropdown: async (params?: { department_id?: number }) => {
    const response = await api.get('/users/vms-dpo', { params });
    return response.data;
  },
  vmsSmfTeamDropdown: async (params?: { department_id?: number }) => {
    const response = await api.get('/users/vms-smf', { params });
    return response.data;
  },
  vmsKycTeamDropdown: async () => {
    const response = await api.get('/users/vms-kyc');
    return response.data;
  },
  vmsAdminsDropdown: async (params?: { department_id?: number }) => {
    const response = await api.get('/users/vms-admins', { params });
    return response.data;
  },
  list: async (params?: {
    status?: string;
    search?: string;
    pending_for_me?: boolean | 1;
    /** Business owner: drafts + needs_more_information only (My Tray). */
    initiator_tray?: boolean | 1;
    page?: number;
    limit?: number;
    vendor_initiation_id?: number;
  }) => {
    const response = await api.get('/rfps', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/rfps/${id}`);
    return response.data?.data ?? response.data;
  },
  getAuditTrail: async (id: number) => {
    const response = await api.get(`/rfps/${id}/audit-trail`);
    return response.data?.data ?? response.data;
  },
  create: async (formData: FormData) => {
    const response = await api.post('/rfps', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.patch(`/rfps/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  approve: async (id: number, body?: { notes?: string; approverAttachments?: File[] }) => {
    const files = body?.approverAttachments ?? [];
    const fd = new FormData();
    if (body?.notes?.trim()) fd.append('notes', body.notes.trim());
    for (const f of files) {
      fd.append('approver_attachments', f);
    }
    const response =
      files.length > 0 || body?.notes?.trim()
        ? await api.post(`/rfps/${id}/approve`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        : await api.post(`/rfps/${id}/approve`, {});
    return response.data;
  },
  reject: async (id: number, body?: { notes?: string }) => {
    const response = await api.post(`/rfps/${id}/reject`, body ?? {});
    return response.data;
  },
  requestNeedMoreInformation: async (id: number, body: { comment: string }) => {
    const response = await api.post(`/rfps/${id}/need-more-information`, body);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/rfps/${id}`);
    return response.data;
  },
  patchApprovers: async (
    id: number,
    body: { business_heads?: { userId: number; level: number }[]; business_head_user_ids?: number[] }
  ) => {
    const response = await api.patch(`/rfps/${id}/approvers`, body);
    return response.data;
  },
};

export const prospectiveVendorsAPI = {
  list: async (params?: {
    page?: number;
    limit?: number;
    q?: string;
    vendor_initiation_id?: number;
    /** Business owner: only draft requests (My Tray). */
    draft_only?: boolean | 1;
  }) => {
    const response = await api.get('/prospective-vendors', { params });
    return response.data;
  },
  listStakeholder: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/prospective-vendors/stakeholder', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/prospective-vendors/${id}`);
    return response.data;
  },
  getAuditTrail: async (id: number) => {
    const response = await api.get(`/prospective-vendors/${id}/audit-trail`);
    return response.data?.data ?? response.data;
  },
  create: async (formData: FormData) => {
    const response = await api.post('/prospective-vendors', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.patch(`/prospective-vendors/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/prospective-vendors/${id}`);
    return response.data;
  },
};

export const vendorProposalsAPI = {
  list: async (params?: {
    page?: number;
    limit?: number;
    vendor_initiation_id?: number;
    /** Business owner: drafts + needs_more_information only (My Tray). */
    initiator_tray?: boolean | 1;
  }) => {
    const response = await api.get('/vendor-proposals', { params });
    return response.data;
  },
  listStakeholder: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/vendor-proposals/stakeholder', { params });
    return response.data;
  },
  /** Submitted or approved proposals for the current user (no pagination). */
  listApprovedForEvaluation: async (params?: { vendor_initiation_id?: number }) => {
    const response = await api.get('/vendor-proposals/approved-for-evaluation', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/vendor-proposals/${id}`);
    return response.data?.data ?? response.data;
  },
  getAuditTrail: async (id: number) => {
    const response = await api.get(`/vendor-proposals/${id}/audit-trail`);
    return response.data?.data ?? response.data;
  },
  create: async (formData: FormData) => {
    const response = await api.post('/vendor-proposals', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.patch(`/vendor-proposals/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/vendor-proposals/${id}`);
    return response.data;
  },
};

export const vendorEvaluationsAPI = {
  list: async (params?: {
    vendor_initiation_id?: number;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const response = await api.get('/vendor-evaluations', { params });
    return response.data;
  },
  /** Fully approved + BH voting complete; includes department_id and vendor lines with vote counts */
  listForFinancialApproval: async (params?: { vendor_initiation_id?: number }) => {
    const response = await api.get('/vendor-evaluations/for-financial-approval', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/vendor-evaluations/${id}`);
    return response.data?.data ?? response.data;
  },
  create: async (formData: FormData) => {
    const response = await api.post('/vendor-evaluations', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.put(`/vendor-evaluations/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  listPendingStakeholder: async () => {
    const response = await api.get('/vendor-evaluations/pending/stakeholder');
    return response.data;
  },
  listPendingBusinessHead: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/vendor-evaluations/pending/business-head', { params });
    return response.data;
  },
  listStakeholderHistory: async () => {
    const response = await api.get('/vendor-evaluations/stakeholder/history');
    return response.data;
  },
  stakeholderDecision: async (id: number, body: { action: 'approve' | 'reject'; notes?: string }) => {
    const response = await api.post(`/vendor-evaluations/${id}/stakeholder-decision`, body);
    return response.data;
  },
  businessHeadVote: async (id: number, body: { vendor_proposal_line_id: number; notes?: string }) => {
    const response = await api.post(`/vendor-evaluations/${id}/business-head-vote`, body);
    return response.data;
  },
  approverDecision: async (
    id: number,
    body: { action: 'approve' | 'reject'; notes?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    if (files.length === 0) {
      const response = await api.post(`/vendor-evaluations/${id}/approver-decision`, {
        action: body.action,
        notes: body.notes,
      });
      return response.data;
    }
    const fd = new FormData();
    fd.append('action', body.action);
    if (body.notes?.trim()) fd.append('notes', body.notes.trim());
    for (const f of files) {
      fd.append('approver_attachments', f);
    }
    const response = await api.post(`/vendor-evaluations/${id}/approver-decision`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  requestNeedMoreInformation: async (id: number, body: { comment: string }) => {
    const response = await api.post(`/vendor-evaluations/${id}/need-more-information`, body);
    return response.data;
  },
  patchApprovers: async (id: number, body: { business_head_user_ids: number[] }) => {
    const response = await api.patch(`/vendor-evaluations/${id}/approvers`, body);
    return response.data;
  },
};

export const financialApprovalsAPI = {
  list: async (params?: {
    page?: number;
    limit?: number;
    vendor_initiation_id?: number;
    /** Business head: only FAs where your approver step is pending (My Tray). */
    pending_for_me?: boolean | 1;
    /** Business owner: drafts + needs_more_information only (My Tray). */
    initiator_tray?: boolean | 1;
  }) => {
    const clean: Record<string, string | number> = {};
    if (params?.page != null) clean.page = params.page;
    if (params?.limit != null) clean.limit = params.limit;
    if (params?.vendor_initiation_id != null) clean.vendor_initiation_id = params.vendor_initiation_id;
    if (params?.pending_for_me === true || params?.pending_for_me === 1) clean.pending_for_me = '1';
    if (params?.initiator_tray === true || params?.initiator_tray === 1) clean.initiator_tray = '1';
    const response = await api.get('/financial-approvals', { params: clean });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/financial-approvals/${id}`);
    return response.data?.data ?? response.data;
  },
  getAuditTrail: async (id: number) => {
    const response = await api.get(`/financial-approvals/${id}/audit-trail`);
    return response.data?.data ?? response.data;
  },
  create: async (formData: FormData) => {
    const response = await api.post('/financial-approvals', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.put(`/financial-approvals/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  requestNeedMoreInformation: async (id: number, body: { comment: string }) => {
    const response = await api.post(`/financial-approvals/${id}/need-more-information`, body);
    return response.data;
  },
  approve: async (id: number, body?: { notes?: string; approverAttachments?: File[] | null }) => {
    const fd = new FormData();
    if (body?.notes?.trim()) fd.append('notes', body.notes.trim());
    for (const f of body?.approverAttachments ?? []) {
      fd.append('approver_attachments', f);
    }
    const response = await api.post(`/financial-approvals/${id}/approve`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  reject: async (id: number, body?: { notes?: string }) => {
    const response = await api.post(`/financial-approvals/${id}/reject`, body ?? {});
    return response.data;
  },
  patchApprovers: async (id: number, body: { business_head_user_ids: number[] }) => {
    const response = await api.patch(`/financial-approvals/${id}/approvers`, body);
    return response.data;
  },
};

export type VmsDpiaManagementRegistryRow = {
  vms_registered_vendor_id: number;
  vendor_initiation_id: number | null;
  /** @deprecated Use pnb_reference_number — kept for backward compatibility. */
  vendor_id: string;
  /** Outsourcing register PNB reference, e.g. PNB-0018 */
  pnb_reference_number?: string;
  vendor_name: string;
  service: string;
  department: string;
  business_rm: string;
  what_data_used: string;
  purpose_of_use: string;
  role_processor_controller: string;
  dpa_contract: string;
  international_data_transfers: string;
};

export type ContractManagementDocSlot =
  | 'contract_document'
  | 'sow'
  | 'amendments'
  | 'addendums'
  | 'supporting_documents';

export type ContractManagementFileMeta = {
  url: string;
  originalName: string;
  size: number;
  uploadedAt: string;
  storedName: string;
};

export type ContractManagementDocuments = Record<ContractManagementDocSlot, ContractManagementFileMeta[]>;

export type VmsContractManagementRegistryRow = {
  vms_registered_vendor_id: number;
  vendor_initiation_id: number | null;
  vendor_id: string;
  pnb_reference_number?: string;
  vendor_register_number?: string;
  vendor_name: string;
  service: string;
  business_owner: string;
  service_start_date: string;
  last_reviewed: string;
  next_review: string;
  renewal_date: string;
  perpetual: string;
  status: string;
};

export type VmsContractManagementRecord = {
  vms_registered_vendor_id: number;
  vendor_id: string;
  pnb_reference_number?: string;
  vendor_register_number: string;
  vendor_name: string;
  business_owner: string;
  service_name: string;
  form_values: Record<string, string>;
  documents: ContractManagementDocuments;
};

export const vmsRegisteredVendorsAPI = {
  list: async (params?: { page?: number; limit?: number; vendor_initiation_id?: number }) => {
    const response = await api.get('/vms-registered-vendors', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/vms-registered-vendors/${id}`);
    return response.data?.data ?? response.data;
  },
  getOnboardingPipelineStatus: async (registeredVendorId: number) => {
    const response = await api.get(
      `/vms-registered-vendors/${registeredVendorId}/onboarding-pipeline-status`
    );
    return response.data?.data ?? response.data;
  },
  postOnboardingPipelineStatusBatch: async (ids: number[]) => {
    const response = await api.post('/vms-registered-vendors/onboarding-pipeline-status/batch', {
      ids,
    });
    return (response.data?.data ?? response.data) as Record<
      string,
      VmsOnboardingPipelineSummary
    >;
  },
  getOnboardingAdditional: async (vendorInitiationId: number) => {
    const response = await api.get('/vms-registered-vendors/onboarding-additional', {
      params: { vendor_initiation_id: vendorInitiationId },
    });
    return response.data;
  },
  patchOnboardingAdditional: async (registeredVendorId: number, payload: Record<string, unknown>) => {
    const response = await api.patch(
      `/vms-registered-vendors/${registeredVendorId}/onboarding-additional`,
      payload
    );
    return response.data;
  },
  getRegisterFillingPreview: async (registeredVendorId: number) => {
    const response = await api.get(
      `/vms-registered-vendors/${registeredVendorId}/register-filling-preview`
    );
    return (response.data?.data ?? response.data) as VmsOutsourcingVendorDetail;
  },
  getRegisterFilling: async (registeredVendorId: number) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/register-filling`);
    return (response.data?.data ?? response.data) as {
      preview: VmsOutsourcingVendorDetail;
      item: VmsRegisterFillingWorkflowItem | null;
    };
  },
  patchRegisterFilling: async (registeredVendorId: number, body: Record<string, unknown>) => {
    const response = await api.patch(
      `/vms-registered-vendors/${registeredVendorId}/register-filling`,
      body
    );
    return response.data;
  },
  putRegisterFillingConfig: async (registeredVendorId: number, approverIds: number[]) => {
    const response = await api.put(
      `/vms-registered-vendors/${registeredVendorId}/register-filling/config`,
      { approver_ids: approverIds }
    );
    return response.data;
  },
  submitRegisterFilling: async (registeredVendorId: number) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/register-filling/submit`
    );
    return response.data;
  },
  resubmitRegisterFilling: async (registeredVendorId: number) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/register-filling/resubmit`
    );
    return response.data;
  },
  approveRegisterFilling: async (registeredVendorId: number) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/register-filling/approve`
    );
    return response.data;
  },
  needMoreInfoRegisterFilling: async (registeredVendorId: number, comment: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/register-filling/need-more-information`,
      { comment }
    );
    return response.data;
  },
  getNotificationRegulator: async (registeredVendorId: number) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/notification-regulator`);
    return (response.data?.data ?? response.data) as {
      id?: number;
      status: string;
      applicability?: 'applicable' | 'not_applicable' | null;
      not_applicable_reason?: string | null;
      form: Record<string, unknown>;
      workflow_status?: string;
      approved_workflow_status?: string;
      pending_workflow_status?: string | null;
      submitted_at?: string | null;
      is_complete?: boolean;
      prerequisites_complete?: boolean;
      can_submit?: boolean;
      can_edit?: boolean;
      can_approve?: boolean;
      configured_approver_ids?: number[];
      needs_more_info_comment?: string | null;
      approver_feedback?: {
        id: number;
        approver_id: number;
        first_name?: string;
        last_name?: string;
        email?: string;
        action_type: 'approve' | 'need_more_information';
        comment: string | null;
        created_at: string | null;
        attachments: {
          id: number;
          file_name: string;
          file_size?: number | null;
          download_url: string;
        }[];
      }[];
      approvers?: {
        approver_id: number;
        status: string;
        first_name?: string;
        last_name?: string;
        email?: string;
        display_name?: string;
      }[];
      my_approver_row_id?: number | null;
      my_approver_status?: string | null;
    };
  },
  patchNotificationRegulatorApplicability: async (
    registeredVendorId: number,
    applicability: 'applicable' | 'not_applicable',
    notApplicableReason?: string | null
  ) => {
    const body: Record<string, unknown> = { applicability };
    if (notApplicableReason !== undefined) {
      body.not_applicable_reason = notApplicableReason;
    }
    const response = await api.patch(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/applicability`,
      body
    );
    return response.data;
  },
  putNotificationRegulatorConfig: async (registeredVendorId: number, approverIds: number[]) => {
    const response = await api.put(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/config`,
      { approver_ids: approverIds }
    );
    return response.data;
  },
  saveNotificationRegulatorDraft: async (registeredVendorId: number, body: Record<string, unknown>) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/draft`,
      body
    );
    return response.data;
  },
  uploadNotificationRegulatorFinalCopy: async (registeredVendorId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/final-copy`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return (response.data?.data ?? response.data) as {
      final_copy_files: Record<string, unknown>[];
    };
  },
  deleteNotificationRegulatorFinalCopy: async (registeredVendorId: number, storedName: string) => {
    const response = await api.delete(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/final-copy/${encodeURIComponent(storedName)}`
    );
    return (response.data?.data ?? response.data) as {
      final_copy_files: Record<string, unknown>[];
    };
  },
  submitNotificationRegulator: async (
    registeredVendorId: number,
    body: Record<string, unknown> & { approver_ids?: number[] }
  ) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/submit`,
      body
    );
    return response.data as { success?: boolean; completed?: boolean; message?: string };
  },
  resubmitNotificationRegulator: async (
    registeredVendorId: number,
    body: Record<string, unknown> & { approver_ids?: number[] }
  ) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/resubmit`,
      body
    );
    return response.data as { success?: boolean; message?: string };
  },
  approveNotificationRegulator: async (
    registeredVendorId: number,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    if (files.length > 0 || note) {
      const fd = new FormData();
      if (note) fd.append('approver_comment', note);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/notification-regulator/approve`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data as { success?: boolean; completed?: boolean; message?: string };
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/approve`
    );
    return response.data as { success?: boolean; completed?: boolean; message?: string };
  },
  needMoreInfoNotificationRegulator: async (
    registeredVendorId: number,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      if (extraNote) fd.append('approver_comment', extraNote);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/notification-regulator/need-more-information`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/notification-regulator/need-more-information`,
      { comment: body.comment }
    );
    return response.data;
  },
  listPendingAnnexuresForMe: async (params?: { page?: number; limit?: number; pending_only?: 0 | 1 | boolean }) => {
    const response = await api.get('/vms-registered-vendors/annexures/pending-for-me', { params });
    return response.data;
  },
  /** Admin My Tray: registered vendors waiting for onboarding annexure applicability setup. */
  listPendingOnboardingAnnexureSetupForAdmin: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/vms-registered-vendors/onboarding-annexure-setup/pending-for-admin', {
      params,
    });
    return response.data;
  },
  listInitiatorTrayOngoingAnnexuresForMe: async () => {
    const response = await api.get('/vms-registered-vendors/annexures/initiator-tray-for-me');
    return response.data;
  },
  getAnnexures: async (registeredVendorId: number) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/annexures`);
    return (response.data?.data ?? response.data) as Record<string, unknown>;
  },
  patchAnnexuresConfig: async (
    registeredVendorId: number,
    items: {
      code: string;
      applicability: 'applicable' | 'not_applicable';
      approver_ids: number[];
      not_applicable_reason?: string | null;
    }[]
  ) => {
    const response = await api.patch(`/vms-registered-vendors/${registeredVendorId}/annexures/config`, {
      items: items.map((i) => ({
        code: i.code,
        applicability: i.applicability,
        approver_ids: i.approver_ids,
        ...(Object.prototype.hasOwnProperty.call(i, 'not_applicable_reason')
          ? { not_applicable_reason: i.not_applicable_reason ?? null }
          : {}),
      })),
    });
    return response.data;
  },
  putAnnexureResponse: async (registeredVendorId: number, code: string, responseBody: Record<string, unknown>) => {
    const response = await api.put(`/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/response`, {
      response: responseBody,
    });
    return response.data;
  },
  uploadAnnexureRowAttachment: async (
    registeredVendorId: number,
    code: string,
    rowId: string,
    file: File
  ): Promise<{
    url: string;
    originalName: string;
    size: number;
    uploadedAt: string;
    storedName: string;
  }> => {
    const fd = new FormData();
    fd.append('rowId', rowId);
    fd.append('file', file);
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/row-attachment`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const data = response.data?.data ?? response.data;
    return data as {
      url: string;
      originalName: string;
      size: number;
      uploadedAt: string;
      storedName: string;
    };
  },
  listAnnexureRowComments: async (
    registeredVendorId: number,
    code: string
  ): Promise<AnnexureRowApproverComment[]> => {
    const response = await api.get(
      `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/row-comments`
    );
    return ((response.data?.data ?? []) as AnnexureRowApproverComment[]).map((r) => ({
      id: Number(r.id),
      row_id: String(r.row_id),
      approver_id: Number(r.approver_id),
      comment: String(r.comment ?? ''),
      updated_at: r.updated_at == null ? null : String(r.updated_at),
    }));
  },
  upsertAnnexureRowComment: async (
    registeredVendorId: number,
    code: string,
    body: { rowId: string; comment: string }
  ) => {
    const response = await api.put(
      `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/row-comments`,
      body
    );
    return response.data;
  },
  submitAnnexure: async (registeredVendorId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/submit`
    );
    return response.data;
  },
  resubmitAnnexure: async (registeredVendorId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/resubmit`
    );
    return response.data;
  },
  approveRegisteredVendorAnnexure: async (
    registeredVendorId: number,
    code: string,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    if (files.length > 0 || note) {
      const fd = new FormData();
      if (note) fd.append('approver_comment', note);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/approve`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/approve`
    );
    return response.data;
  },
  needMoreInfoRegisteredVendorAnnexure: async (
    registeredVendorId: number,
    code: string,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      if (extraNote) fd.append('approver_comment', extraNote);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/need-more-information`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/annexures/${encodeURIComponent(code)}/need-more-information`,
      { comment: body.comment }
    );
    return response.data;
  },
  createOngoingAnnexureBatch: async (
    registeredVendorId: number,
    body: { assessment_type: 'periodic' | 'monthly'; vms_outsourcing_vendor_id?: number }
  ) => {
    const response = await api.post(`/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches`, body);
    return response.data;
  },
  listOngoingAnnexureBatches: async (registeredVendorId: number, params?: { assessment_type?: string }) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches`, {
      params,
    });
    return response.data;
  },
  getOngoingAnnexuresForBatch: async (registeredVendorId: number, batchId: number) => {
    const response = await api.get(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures`
    );
    return (response.data?.data ?? response.data) as Record<string, unknown>;
  },
  patchOngoingAnnexureBatchConfig: async (
    registeredVendorId: number,
    batchId: number,
    items: { code: string; applicability: 'applicable' | 'not_applicable'; approver_ids: number[] }[]
  ) => {
    const response = await api.patch(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/config`,
      { items: items.map((i) => ({ code: i.code, applicability: i.applicability, approver_ids: i.approver_ids })) }
    );
    return response.data;
  },
  putOngoingAnnexureBatchResponse: async (
    registeredVendorId: number,
    batchId: number,
    code: string,
    responseBody: Record<string, unknown>
  ) => {
    const response = await api.put(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/response`,
      { response: responseBody }
    );
    return response.data;
  },
  uploadOngoingAnnexureRowAttachment: async (
    registeredVendorId: number,
    batchId: number,
    code: string,
    rowId: string,
    file: File
  ): Promise<{
    url: string;
    originalName: string;
    size: number;
    uploadedAt: string;
    storedName: string;
  }> => {
    const fd = new FormData();
    fd.append('rowId', rowId);
    fd.append('file', file);
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/row-attachment`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const data = response.data?.data ?? response.data;
    return data as {
      url: string;
      originalName: string;
      size: number;
      uploadedAt: string;
      storedName: string;
    };
  },
  listOngoingAnnexureRowComments: async (
    registeredVendorId: number,
    batchId: number,
    code: string
  ): Promise<AnnexureRowApproverComment[]> => {
    const response = await api.get(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(
        code
      )}/row-comments`
    );
    return ((response.data?.data ?? []) as AnnexureRowApproverComment[]).map((r) => ({
      id: Number(r.id),
      row_id: String(r.row_id),
      approver_id: Number(r.approver_id),
      comment: String(r.comment ?? ''),
      updated_at: r.updated_at == null ? null : String(r.updated_at),
    }));
  },
  upsertOngoingAnnexureRowComment: async (
    registeredVendorId: number,
    batchId: number,
    code: string,
    body: { rowId: string; comment: string }
  ) => {
    const response = await api.put(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(
        code
      )}/row-comments`,
      body
    );
    return response.data;
  },
  submitOngoingAnnexureBatchAnnexure: async (registeredVendorId: number, batchId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/submit`
    );
    return response.data;
  },
  resubmitOngoingAnnexureBatchAnnexure: async (registeredVendorId: number, batchId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/resubmit`
    );
    return response.data;
  },
  approveOngoingAnnexureBatchAnnexure: async (
    registeredVendorId: number,
    batchId: number,
    code: string,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    const url = `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/approve`;
    if (files.length > 0 || note) {
      const fd = new FormData();
      if (note) fd.append('approver_comment', note);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    }
    const response = await api.post(url);
    return response.data;
  },
  needMoreInfoOngoingAnnexureBatchAnnexure: async (
    registeredVendorId: number,
    batchId: number,
    code: string,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    const url = `/vms-registered-vendors/${registeredVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/need-more-information`;
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      if (extraNote) fd.append('approver_comment', extraNote);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    }
    const response = await api.post(url, { comment: body.comment });
    return response.data;
  },
  listPendingDpiaForMe: async (params?: { page?: number; limit?: number; pending_only?: 0 | 1 | boolean }) => {
    const response = await api.get('/vms-registered-vendors/dpia/pending-for-me', { params });
    return response.data;
  },
  listInitiatorTrayDpiaForMe: async () => {
    const response = await api.get('/vms-registered-vendors/dpia/initiator-tray-for-me');
    return response.data as {
      success?: boolean;
      data?: {
        item_id: number;
        item_status: string;
        dpia_code: string;
        dpia_title: string | null;
        needs_more_info_comment: string | null;
        vms_registered_vendor_id: number;
        vendor_display_name: string;
        vendor_initiation_id: number | null;
        financial_approval_id: number | null;
        updated_at: string | null;
      }[];
    };
  },
  listDpiaManagementRegistry: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get('/vms-registered-vendors/dpia/management-registry', { params });
    return response.data as {
      success?: boolean;
      data?: VmsDpiaManagementRegistryRow[];
      pagination?: { page: number; limit: number; total: number; total_pages: number };
    };
  },
  patchDpiaManagementRegistry: async (
    registeredVendorId: number,
    body: Pick<
      VmsDpiaManagementRegistryRow,
      | 'what_data_used'
      | 'purpose_of_use'
      | 'role_processor_controller'
      | 'dpa_contract'
      | 'international_data_transfers'
    >
  ) => {
    const response = await api.patch(
      `/vms-registered-vendors/${registeredVendorId}/dpia/management-registry`,
      body
    );
    return response.data as {
      success?: boolean;
      message?: string;
      data?: VmsDpiaManagementRegistryRow;
    };
  },
  listContractManagementRegistry: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get('/vms-registered-vendors/contract-management/registry', { params });
    return response.data as {
      success?: boolean;
      data?: VmsContractManagementRegistryRow[];
      pagination?: { page: number; limit: number; total: number; total_pages: number };
    };
  },
  exportContractManagement: async (params?: { search?: string }) => {
    const response = await api.get('/vms-registered-vendors/contract-management/export-data', { params });
    return response.data as { success?: boolean; data?: Record<string, string>[]; message?: string };
  },
  importContractManagement: async (file: File, registeredVendorId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    const params =
      registeredVendorId != null && Number.isInteger(registeredVendorId) && registeredVendorId > 0
        ? { registered_vendor_id: registeredVendorId }
        : undefined;
    const response = await api.post('/vms-registered-vendors/contract-management/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
    return response.data as {
      success?: boolean;
      message?: string;
      data?: {
        updated_count?: number;
        skipped_count?: number;
        skipped?: Array<{ row: number; vendor: string; reason: string }>;
      };
    };
  },
  getContractManagement: async (registeredVendorId: number) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/contract-management`);
    return response.data as { success?: boolean; data?: VmsContractManagementRecord };
  },
  patchContractManagement: async (
    registeredVendorId: number,
    formValues: Record<string, string>,
    options?: { registryPatch?: boolean },
  ) => {
    const response = await api.patch(`/vms-registered-vendors/${registeredVendorId}/contract-management`, {
      form_values: formValues,
      registry_patch: options?.registryPatch === true,
    });
    return response.data as {
      success?: boolean;
      message?: string;
      data?: { form_values: Record<string, string>; documents: ContractManagementDocuments };
    };
  },
  uploadContractManagementDocument: async (
    registeredVendorId: number,
    slot: ContractManagementDocSlot,
    file: File
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/contract-management/documents/${encodeURIComponent(slot)}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data as {
      success?: boolean;
      data?: ContractManagementFileMeta;
      documents?: ContractManagementDocuments;
    };
  },
  deleteContractManagementDocument: async (
    registeredVendorId: number,
    slot: ContractManagementDocSlot,
    storedName: string
  ) => {
    const response = await api.delete(
      `/vms-registered-vendors/${registeredVendorId}/contract-management/documents/${encodeURIComponent(slot)}/${encodeURIComponent(storedName)}`
    );
    return response.data as { success?: boolean; documents?: ContractManagementDocuments };
  },
  getDpia: async (registeredVendorId: number) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/dpia`);
    return response.data;
  },
  patchDpiaConfig: async (
    registeredVendorId: number,
    items: {
      code: string;
      applicability: 'applicable' | 'not_applicable';
      approver_ids: number[];
      not_applicable_reason?: string | null;
    }[]
  ) => {
    const response = await api.patch(`/vms-registered-vendors/${registeredVendorId}/dpia/config`, {
      items: items.map((i) => ({
        code: i.code,
        applicability: i.applicability,
        approver_ids: i.approver_ids,
        ...(Object.prototype.hasOwnProperty.call(i, 'not_applicable_reason')
          ? { not_applicable_reason: i.not_applicable_reason ?? null }
          : {}),
      })),
    });
    return response.data;
  },
  putDpiaResponse: async (registeredVendorId: number, code: string, responseBody: Record<string, unknown>) => {
    const response = await api.put(
      `/vms-registered-vendors/${registeredVendorId}/dpia/${encodeURIComponent(code)}/response`,
      { response: responseBody }
    );
    return response.data;
  },
  submitDpia: async (registeredVendorId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/dpia/${encodeURIComponent(code)}/submit`
    );
    return response.data;
  },
  resubmitDpia: async (registeredVendorId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/dpia/${encodeURIComponent(code)}/resubmit`
    );
    return response.data;
  },
  approveRegisteredVendorDpia: async (
    registeredVendorId: number,
    code: string,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    if (files.length > 0 || note) {
      const fd = new FormData();
      if (note) fd.append('approver_comment', note);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/dpia/${encodeURIComponent(code)}/approve`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/dpia/${encodeURIComponent(code)}/approve`
    );
    return response.data;
  },
  needMoreInfoRegisteredVendorDpia: async (
    registeredVendorId: number,
    code: string,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      if (extraNote) fd.append('approver_comment', extraNote);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/dpia/${encodeURIComponent(code)}/need-more-information`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/dpia/${encodeURIComponent(code)}/need-more-information`,
      { comment: body.comment }
    );
    return response.data;
  },
  listPendingContractsForMe: async (params?: { page?: number; limit?: number; pending_only?: 0 | 1 | boolean }) => {
    const response = await api.get('/vms-registered-vendors/contracts/pending-for-me', { params });
    return response.data;
  },
  listPendingUnifiedContractReviewForMe: async (params?: {
    page?: number;
    limit?: number;
    pending_only?: 0 | 1 | boolean;
  }) => {
    const response = await api.get('/vms-registered-vendors/unified-contract-review/pending-for-me', { params });
    return response.data;
  },
  listPendingRegisterFillingForMe: async (params?: {
    page?: number;
    limit?: number;
    pending_only?: 0 | 1 | boolean;
  }) => {
    const response = await api.get('/vms-registered-vendors/register-filling/pending-for-me', { params });
    return response.data;
  },
  listPendingNotificationRegulatorForMe: async (params?: {
    page?: number;
    limit?: number;
    pending_only?: 0 | 1 | boolean;
  }) => {
    const response = await api.get('/vms-registered-vendors/notification-regulator/pending-for-me', { params });
    return response.data;
  },
  listInitiatorTrayRegisterFillingForMe: async () => {
    const response = await api.get('/vms-registered-vendors/register-filling/initiator-tray-for-me');
    return response.data;
  },
  getUnifiedContractReview: async (registeredVendorId: number) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/unified-contract-review`);
    return response.data;
  },
  patchUnifiedContractReviewConfig: async (registeredVendorId: number, body: { approver_ids: number[] }) => {
    const response = await api.patch(
      `/vms-registered-vendors/${registeredVendorId}/unified-contract-review/config`,
      body
    );
    return response.data;
  },
  putUnifiedContractReviewResponse: async (registeredVendorId: number, responseBody: Record<string, unknown>) => {
    const response = await api.put(`/vms-registered-vendors/${registeredVendorId}/unified-contract-review/response`, {
      response: responseBody,
    });
    return response.data;
  },
  submitUnifiedContractReview: async (registeredVendorId: number) => {
    const response = await api.post(`/vms-registered-vendors/${registeredVendorId}/unified-contract-review/submit`);
    return response.data;
  },
  resubmitUnifiedContractReview: async (registeredVendorId: number) => {
    const response = await api.post(`/vms-registered-vendors/${registeredVendorId}/unified-contract-review/resubmit`);
    return response.data;
  },
  approveUnifiedContractReview: async (
    registeredVendorId: number,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    if (files.length > 0 || note) {
      const fd = new FormData();
      if (note) fd.append('approver_comment', note);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/unified-contract-review/approve`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(`/vms-registered-vendors/${registeredVendorId}/unified-contract-review/approve`);
    return response.data;
  },
  needMoreInfoUnifiedContractReview: async (
    registeredVendorId: number,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      if (extraNote) fd.append('approver_comment', extraNote);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/unified-contract-review/need-more-information`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/unified-contract-review/need-more-information`,
      { comment: body.comment }
    );
    return response.data;
  },
  getContracts: async (registeredVendorId: number) => {
    const response = await api.get(`/vms-registered-vendors/${registeredVendorId}/contracts`);
    return response.data;
  },
  patchContractsConfig: async (
    registeredVendorId: number,
    items: { code: string; applicability: 'applicable' | 'not_applicable'; approver_ids: number[] }[]
  ) => {
    const response = await api.patch(`/vms-registered-vendors/${registeredVendorId}/contracts/config`, {
      items: items.map((i) => ({ code: i.code, applicability: i.applicability, approver_ids: i.approver_ids })),
    });
    return response.data;
  },
  putContractResponse: async (registeredVendorId: number, code: string, responseBody: Record<string, unknown>) => {
    const response = await api.put(
      `/vms-registered-vendors/${registeredVendorId}/contracts/${encodeURIComponent(code)}/response`,
      { response: responseBody }
    );
    return response.data;
  },
  submitContract: async (registeredVendorId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/contracts/${encodeURIComponent(code)}/submit`
    );
    return response.data;
  },
  resubmitContract: async (registeredVendorId: number, code: string) => {
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/contracts/${encodeURIComponent(code)}/resubmit`
    );
    return response.data;
  },
  approveRegisteredVendorContract: async (
    registeredVendorId: number,
    code: string,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    if (files.length > 0 || note) {
      const fd = new FormData();
      if (note) fd.append('approver_comment', note);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/contracts/${encodeURIComponent(code)}/approve`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/contracts/${encodeURIComponent(code)}/approve`
    );
    return response.data;
  },
  needMoreInfoRegisteredVendorContract: async (
    registeredVendorId: number,
    code: string,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      if (extraNote) fd.append('approver_comment', extraNote);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(
        `/vms-registered-vendors/${registeredVendorId}/contracts/${encodeURIComponent(code)}/need-more-information`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vms-registered-vendors/${registeredVendorId}/contracts/${encodeURIComponent(code)}/need-more-information`,
      { comment: body.comment }
    );
    return response.data;
  },
};

/** Row from GET /vms-outsourcing-vendors (list). */
export type VmsOutsourcingVendorListRow = {
  id: number;
  vms_registered_vendor_id?: number | null;
  financial_approval_id?: number | null;
  vendor_display_name: string;
  outsourcing_register_number?: string | null;
  vendor_initiation_id?: number | null;
  department_id?: number | null;
  department_name?: string | null;
  /** Department RM — vendor initiation creator (business owner role). */
  vendor_rm_name?: string | null;
  vendor_rm_user_id?: number | null;
  imported_manually?: boolean | number;
  register_schema_version?: number | null;
  outsourcing_service_description?: string | null;
  business_registration_number?: string | null;
  country_iso_alpha3?: string | null;
  materiality_assessment_ymd?: string | null;
  /** From snapshot_json — month source for Periodic Review Month list column. */
  last_periodic_review_ymd?: string | null;
  /** From snapshot_json — linked to Ongoing Management previous periodic review date. */
  last_performance_review_ymd?: string | null;
  contract_commencement_raw?: string | null;
  last_contract_renewal_ymd?: string | null;
  contract_expiry_raw?: string | null;
  fa_reference_number?: string | null;
  service_project_name?: string | null;
  /** Vendor initiation service name — canonical label across VMS. */
  service_name?: string | null;
  fa_status?: string | null;
  /** Register lifecycle status (active / inactive / exit). */
  register_status?: string | null;
  /** Current/derived exit planning status for this outsourcing vendor. */
  exit_planning_status?: string | null;
  created_at?: string;
  /** From linked vendor initiation (`service_type`). */
  service_type?: string | null;
  /** From linked vendor initiation; null = not set */
  is_material?: boolean | null;
};

/** Detail from GET /vms-outsourcing-vendors/:id (register fields + slim snapshot). */
export type VmsRegisterSupportingDocument = {
  id: number;
  file_name: string;
  file_size?: number | null;
  mime_type?: string | null;
  download_url: string;
  uploaded_by_user_id?: number | null;
  created_at?: string;
};

export type VmsRegisterFillingApproverRow = {
  approver_id: number;
  status: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  display_name?: string;
};

export type VmsRegisterFillingWorkflowItem = {
  id: number;
  status: string;
  configured_approver_ids: number[];
  needs_more_info_comment: string | null;
  approvers: VmsRegisterFillingApproverRow[];
  my_approver_row_id: number | null;
  my_approver_status: string | null;
  approved_at?: string | null;
  updated_at?: string;
};

export type VmsRegisterAmendmentWorkflowItem = VmsRegisterFillingWorkflowItem & {
  proposed_snapshot?: Record<string, unknown> | null;
};

export type VmsOnboardingPipelineSummary = {
  annexure: 'pending' | 'current' | 'done';
  dpia: 'pending' | 'current' | 'done';
  contract: 'pending' | 'current' | 'done';
  notification_regulator: 'pending' | 'current' | 'done';
  register_filling: 'pending' | 'current' | 'done';
  prerequisites_complete: boolean;
  all_complete: boolean;
  dpia_applicable?: boolean;
};

export type VmsOutsourcingVendorDetail = VmsOutsourcingVendorListRow & {
  completed_by_user_id?: number | null;
  updated_at?: string;
  onboarding_pipeline?: VmsOnboardingPipelineSummary | null;
  supporting_documents?: VmsRegisterSupportingDocument[];
  material_subcontractor_name?: string;
  country_iso_alpha3?: string;
  third_party_arrangement?: string;
  time_critical_operations?: string;
  subcontracting_details?: string;
  service_provider_substitutability?: string;
  alternate_provider_identified?: string;
  last_contract_renewal_ymd?: string;
  business_registration_number?: string;
  materiality_annexure7?: Record<string, unknown>;
  materiality_assessment_ymd?: string;
  customer_data_sla_summary?: string;
  snapshot?: Record<string, unknown> | null;
};

export const vmsOutsourcingVendorsAPI = {
  list: async (params?: {
    page?: number;
    limit?: number;
    vendor_initiation_id?: number;
    search?: string;
    status?: string;
    material?: string;
    register_status?: string;
    department_id?: string;
    service_type?: string;
    review_month?: string;
    /** Ongoing Management: only vendors assigned to the logged-in RM (not whole department). */
    assigned_rm_only?: 0 | 1 | boolean;
  }) => {
    const response = await api.get('/vms-outsourcing-vendors', { params });
    return response.data;
  },
  getById: async (id: number): Promise<VmsOutsourcingVendorDetail> => {
    const response = await api.get(`/vms-outsourcing-vendors/${id}`);
    return (response.data?.data ?? response.data) as VmsOutsourcingVendorDetail;
  },
  updateRegister: async (
    id: number,
    body: Record<string, unknown>,
  ): Promise<{ success?: boolean; message?: string }> => {
    const response = await api.patch(`/vms-outsourcing-vendors/${id}/register`, body);
    return response.data;
  },
  uploadRegisterSupportingDocuments: async (
    id: number,
    files: File[],
  ): Promise<{
    success?: boolean;
    message?: string;
    data?: { supporting_documents: VmsRegisterSupportingDocument[] };
    supporting_documents?: VmsRegisterSupportingDocument[];
  }> => {
    const formData = new FormData();
    for (const f of files) {
      formData.append('supporting_documents', f);
    }
    const response = await api.post(`/vms-outsourcing-vendors/${id}/register/supporting-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteRegisterSupportingDocument: async (
    id: number,
    attachmentId: number,
  ): Promise<{
    success?: boolean;
    message?: string;
    data?: { supporting_documents: VmsRegisterSupportingDocument[] };
    supporting_documents?: VmsRegisterSupportingDocument[];
  }> => {
    const response = await api.delete(
      `/vms-outsourcing-vendors/${id}/register/supporting-documents/${attachmentId}`,
    );
    return response.data;
  },
  getRegisterAmendment: async (id: number) => {
    const response = await api.get(`/vms-outsourcing-vendors/${id}/register-amendment`);
    return (response.data?.data ?? response.data) as {
      item: VmsRegisterAmendmentWorkflowItem | null;
      is_assigned_rm: boolean;
      can_edit: boolean;
      can_review: boolean;
    };
  },
  patchRegisterAmendment: async (id: number, body: Record<string, unknown>) => {
    const response = await api.patch(`/vms-outsourcing-vendors/${id}/register-amendment`, body);
    return response.data;
  },
  putRegisterAmendmentConfig: async (id: number, approverIds: number[]) => {
    const response = await api.put(`/vms-outsourcing-vendors/${id}/register-amendment/config`, {
      approver_ids: approverIds,
    });
    return response.data;
  },
  submitRegisterAmendment: async (id: number) => {
    const response = await api.post(`/vms-outsourcing-vendors/${id}/register-amendment/submit`);
    return response.data;
  },
  resubmitRegisterAmendment: async (id: number) => {
    const response = await api.post(`/vms-outsourcing-vendors/${id}/register-amendment/resubmit`);
    return response.data;
  },
  approveRegisterAmendment: async (id: number) => {
    const response = await api.post(`/vms-outsourcing-vendors/${id}/register-amendment/approve`);
    return response.data;
  },
  needMoreInfoRegisterAmendment: async (id: number, comment: string) => {
    const response = await api.post(`/vms-outsourcing-vendors/${id}/register-amendment/need-more-information`, {
      comment,
    });
    return response.data;
  },
  listPendingRegisterAmendmentForMe: async (params?: { page?: number; limit?: number; pending_only?: boolean }) => {
    const response = await api.get('/vms-outsourcing-vendors/register-amendment/pending-for-me', { params });
    return response.data;
  },
  listInitiatorTrayRegisterAmendmentForMe: async () => {
    const response = await api.get('/vms-outsourcing-vendors/register-amendment/initiator-tray-for-me');
    return response.data;
  },
  bulkUpload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/vms-outsourcing-vendors/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as {
      success?: boolean;
      message?: string;
      data?: {
        created_count?: number;
        skipped_count?: number;
        skipped?: Array<{ row: number; vendor: string; reason: string }>;
      };
    };
  },
  createOngoingAnnexureBatch: async (
    outsourcingVendorId: number,
    body: { assessment_type: 'periodic' | 'monthly' }
  ) => {
    const response = await api.post(`/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches`, body);
    return response.data;
  },
  listOngoingAnnexureBatches: async (outsourcingVendorId: number, params?: { assessment_type?: string }) => {
    const response = await api.get(`/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches`, {
      params,
    });
    return response.data;
  },
  listOngoingAnnexureBatchesBulk: async (vendorIds: number[]) => {
    const uniqueIds = [...new Set(vendorIds.filter((id) => Number.isInteger(id) && id > 0))];
    if (uniqueIds.length === 0) {
      return { success: true, data: {} as Record<string, unknown[]> };
    }
    const response = await api.get('/vms-outsourcing-vendors/ongoing-annexure-batches/bulk-summary', {
      params: { vendor_ids: uniqueIds.join(',') },
    });
    return response.data as { success?: boolean; data?: Record<string, unknown[]> };
  },
  getOngoingAnnexuresForBatch: async (outsourcingVendorId: number, batchId: number) => {
    const response = await api.get(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures`
    );
    return (response.data?.data ?? response.data) as Record<string, unknown>;
  },
  patchOngoingAnnexureBatchConfig: async (
    outsourcingVendorId: number,
    batchId: number,
    items: { code: string; applicability: 'applicable' | 'not_applicable'; approver_ids: number[] }[]
  ) => {
    const response = await api.patch(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/config`,
      { items: items.map((i) => ({ code: i.code, applicability: i.applicability, approver_ids: i.approver_ids })) }
    );
    return response.data;
  },
  putOngoingAnnexureBatchResponse: async (
    outsourcingVendorId: number,
    batchId: number,
    code: string,
    responseBody: Record<string, unknown>
  ) => {
    const response = await api.put(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/response`,
      { response: responseBody }
    );
    return response.data;
  },
  uploadOngoingAnnexureRowAttachment: async (
    outsourcingVendorId: number,
    batchId: number,
    code: string,
    rowId: string,
    file: File
  ): Promise<{
    url: string;
    originalName: string;
    size: number;
    uploadedAt: string;
    storedName: string;
  }> => {
    const fd = new FormData();
    fd.append('rowId', rowId);
    fd.append('file', file);
    const response = await api.post(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/row-attachment`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const data = response.data?.data ?? response.data;
    return data as {
      url: string;
      originalName: string;
      size: number;
      uploadedAt: string;
      storedName: string;
    };
  },
  listOngoingAnnexureRowComments: async (
    outsourcingVendorId: number,
    batchId: number,
    code: string
  ): Promise<AnnexureRowApproverComment[]> => {
    const response = await api.get(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(
        code
      )}/row-comments`
    );
    return ((response.data?.data ?? []) as AnnexureRowApproverComment[]).map((r) => ({
      id: Number(r.id),
      row_id: String(r.row_id),
      approver_id: Number(r.approver_id),
      comment: String(r.comment ?? ''),
      updated_at: r.updated_at == null ? null : String(r.updated_at),
    }));
  },
  upsertOngoingAnnexureRowComment: async (
    outsourcingVendorId: number,
    batchId: number,
    code: string,
    body: { rowId: string; comment: string }
  ) => {
    const response = await api.put(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(
        code
      )}/row-comments`,
      body
    );
    return response.data;
  },
  submitOngoingAnnexureBatchAnnexure: async (outsourcingVendorId: number, batchId: number, code: string) => {
    const response = await api.post(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/submit`
    );
    return response.data;
  },
  resubmitOngoingAnnexureBatchAnnexure: async (outsourcingVendorId: number, batchId: number, code: string) => {
    const response = await api.post(
      `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/resubmit`
    );
    return response.data;
  },
  approveOngoingAnnexureBatchAnnexure: async (
    outsourcingVendorId: number,
    batchId: number,
    code: string,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    const url = `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/approve`;
    if (files.length > 0 || note) {
      const fd = new FormData();
      if (note) fd.append('approver_comment', note);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    }
    const response = await api.post(url);
    return response.data;
  },
  needMoreInfoOngoingAnnexureBatchAnnexure: async (
    outsourcingVendorId: number,
    batchId: number,
    code: string,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    const url = `/vms-outsourcing-vendors/${outsourcingVendorId}/ongoing-annexure-batches/${batchId}/annexures/${encodeURIComponent(code)}/need-more-information`;
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      if (extraNote) fd.append('approver_comment', extraNote);
      for (const f of files) fd.append('approver_attachments', f);
      const response = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data;
    }
    const response = await api.post(url, { comment: body.comment });
    return response.data;
  },
};

export type VmsExitStressKind = 'stress' | 'non_stress';

export type VmsVendorExitSubmission = {
  id: number;
  plan_kind: 'exit' | 'bcp' | null;
  exit_stress_kind: 'stress' | 'non_stress' | null;
  /** Selected tabletop variants (one or both). Mirrors DB exit_stress_kinds JSON. */
  exit_stress_kinds?: VmsExitStressKind[];
  status: string;
  needs_more_info_comment: string | null;
  configured_approver_ids: number[];
  /** Exit/BCP notification recipients only — not Flow Stakeholders (RFP). */
  configured_stakeholder_ids: number[];
  created_at?: string;
  updated_at?: string;
};

export type VmsExitTabletopRow = {
  template_row_id: number;
  variant: string;
  sort_order: number;
  phase: string;
  activity: string;
  detailed_actions: string;
  expected_outcome: string;
  evidence_to_produce: string;
  owner: string;
  pass_fail: 'pass' | 'fail' | null;
};

export type VmsVendorExitAttachment = {
  id: number;
  file_name: string;
  attachment_url: string | null;
  created_at?: string;
};

export type VmsFlowStakeholderFeedbackItem = {
  id: number;
  user_id: number;
  author_first_name: string;
  author_last_name: string;
  comment_text: string | null;
  file_name: string | null;
  file_url: string | null;
  created_at?: string;
};

export type VmsVendorExitBundle = {
  vms_outsourcing_vendor: {
    id: number;
    vendor_display_name: string;
    outsourcing_register_number: string | null;
    vendor_initiation_id: number | null;
    financial_approval_id: number;
    vms_registered_vendor_id: number;
  };
  submission: VmsVendorExitSubmission | null;
  attachments: VmsVendorExitAttachment[];
  /** Populated when plan_kind is exit and at least one variant is selected. */
  exit_tabletop: {
    stress: { rows: VmsExitTabletopRow[] } | null;
    non_stress: { rows: VmsExitTabletopRow[] } | null;
  } | null;
  can_edit: boolean;
  has_approved_submission: boolean;
  my_exit_approver_status: string | null;
  submission_approvers?: {
    id: number;
    approver_id: number;
    status: string;
    notes?: string | null;
    approved_at?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  }[];
};

export type VmsVendorExitPendingRow = {
  submission_id: number;
  submission_status: string;
  updated_at?: string | null;
  vms_outsourcing_vendor_id: number;
  vendor_display_name: string;
  outsourcing_register_number?: string | null;
  vendor_initiation_id?: number | null;
  financial_approval_id: number;
  exit_approver_row_id: number;
  my_approver_status: string;
};

export const vmsOutsourcingVendorExitAPI = {
  getBundle: async (outsourcingVendorId: number) => {
    const response = await api.get(`/vms-outsourcing-vendor-exit/${outsourcingVendorId}`);
    return response.data as { success?: boolean; data: VmsVendorExitBundle };
  },
  getNotificationRecipientOptions: async (outsourcingVendorId: number) => {
    const response = await api.get(
      `/vms-outsourcing-vendor-exit/${outsourcingVendorId}/notification-recipient-options`
    );
    return response.data as {
      success?: boolean;
      data: { id: number; email?: string; first_name?: string; last_name?: string }[];
    };
  },
  patchPlan: async (
    outsourcingVendorId: number,
    body: {
      plan_kind: 'exit' | 'bcp';
      exit_stress_kind?: 'stress' | 'non_stress' | null;
      exit_stress_kinds?: VmsExitStressKind[];
    }
  ) => {
    const response = await api.patch(`/vms-outsourcing-vendor-exit/${outsourcingVendorId}/plan`, body);
    return response.data;
  },
  patchApprovers: async (outsourcingVendorId: number, body: { approver_ids: number[] }) => {
    const response = await api.patch(`/vms-outsourcing-vendor-exit/${outsourcingVendorId}/approvers`, body);
    return response.data;
  },
  patchNotificationRecipients: async (outsourcingVendorId: number, body: { user_ids: number[] }) => {
    const response = await api.patch(
      `/vms-outsourcing-vendor-exit/${outsourcingVendorId}/notification-recipients`,
      body
    );
    return response.data;
  },
  patchTabletopResponses: async (
    outsourcingVendorId: number,
    body: { items: { template_row_id: number; pass_fail: 'pass' | 'fail' | null }[] }
  ) => {
    const response = await api.patch(
      `/vms-outsourcing-vendor-exit/${outsourcingVendorId}/tabletop-responses`,
      body
    );
    return response.data;
  },
  postAttachment: async (outsourcingVendorId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/vms-outsourcing-vendor-exit/${outsourcingVendorId}/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },
  deleteAttachment: async (outsourcingVendorId: number, attachmentId: number) => {
    const response = await api.delete(
      `/vms-outsourcing-vendor-exit/${outsourcingVendorId}/attachments/${attachmentId}`
    );
    return response.data;
  },
  submit: async (
    outsourcingVendorId: number,
    body: {
      plan_kind: 'exit' | 'bcp';
      exit_stress_kind?: 'stress' | 'non_stress' | null;
      exit_stress_kinds?: VmsExitStressKind[];
      approver_ids: number[];
      notification_recipient_ids?: number[];
      tabletop_items?: { template_row_id: number; pass_fail: 'pass' | 'fail' | null }[];
    }
  ) => {
    const response = await api.post(`/vms-outsourcing-vendor-exit/${outsourcingVendorId}/submit`, body);
    return response.data;
  },
  resubmit: async (
    outsourcingVendorId: number,
    body: {
      plan_kind: 'exit' | 'bcp';
      exit_stress_kind?: 'stress' | 'non_stress' | null;
      exit_stress_kinds?: VmsExitStressKind[];
      approver_ids: number[];
      notification_recipient_ids?: number[];
      tabletop_items?: { template_row_id: number; pass_fail: 'pass' | 'fail' | null }[];
    }
  ) => {
    const response = await api.post(`/vms-outsourcing-vendor-exit/${outsourcingVendorId}/resubmit`, body);
    return response.data;
  },
  approve: async (outsourcingVendorId: number, body?: { notes?: string }) => {
    const response = await api.post(`/vms-outsourcing-vendor-exit/${outsourcingVendorId}/approve`, body ?? {});
    return response.data;
  },
  needMoreInformation: async (outsourcingVendorId: number, body: { comment: string }) => {
    const response = await api.post(
      `/vms-outsourcing-vendor-exit/${outsourcingVendorId}/need-more-information`,
      body
    );
    return response.data;
  },
  listPendingForMe: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/vms-outsourcing-vendor-exit/pending-for-me', { params });
    return response.data as {
      success?: boolean;
      data: VmsVendorExitPendingRow[];
      pagination?: { page: number; limit: number; total: number; total_pages: number };
    };
  },
};

export const flowStakeholderFeedbackAPI = {
  list: async (params: { vendor_initiation_id: number; context_type: string; context_id: number }) => {
    const response = await api.get('/vms-flow-stakeholder-feedback', { params });
    return response.data as { success?: boolean; data: VmsFlowStakeholderFeedbackItem[] };
  },
  post: async (formData: FormData) => {
    const response = await api.post('/vms-flow-stakeholder-feedback', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export type VmsCertificateAssignee = { id: number; display_name: string };

export type VmsOutsourcingVendorCertificate = {
  id: number;
  vms_outsourcing_vendor_id: number;
  vendor_initiation_id: number;
  certificate_name: string;
  department_id: number | null;
  department_name?: string | null;
  start_date: string | null;
  renewal_date: string;
  notification_reference_date: string | null;
  user_ids: number[];
  users: VmsCertificateAssignee[];
  user_display_summary?: string | null;
  attachment_file_name: string | null;
  attachment_url: string | null;
  created_at?: string;
  updated_at?: string;
};

export type VmsCertificateUserOption = {
  id: number;
  display_name: string;
  email: string | null;
};

export const vmsOutsourcingVendorCertificatesAPI = {
  list: async (params: { vendor_initiation_id: number; vms_outsourcing_vendor_id: number }) => {
    const response = await api.get('/vms-outsourcing-vendor-certificates', { params });
    return response.data as { success?: boolean; data: VmsOutsourcingVendorCertificate[] };
  },
  userOptions: async (params: { vendor_initiation_id: number; vms_outsourcing_vendor_id: number }) => {
    const response = await api.get('/vms-outsourcing-vendor-certificates/user-options', { params });
    return response.data as { success?: boolean; data: VmsCertificateUserOption[] };
  },
  create: async (formData: FormData) => {
    const response = await api.post('/vms-outsourcing-vendor-certificates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as { success?: boolean; data: VmsOutsourcingVendorCertificate };
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.patch(`/vms-outsourcing-vendor-certificates/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as { success?: boolean; data: VmsOutsourcingVendorCertificate };
  },
  delete: async (id: number) => {
    const response = await api.delete(`/vms-outsourcing-vendor-certificates/${id}`);
    return response.data as { success?: boolean; message?: string };
  },
};

export const vendorInitiationsAPI = {
  list: async (params?: {
    status?: string;
    search?: string;
    created_by?: number;
    pending_for_me?: 1 | boolean;
    /** Governance admin: all initiations in BH / annexure approval queue (not scoped to current user). */
    pending_queue?: 1 | boolean;
    /** Governance admin: all vendor initiations (every status). */
    governance_all?: 1 | boolean;
    assigned_to_me?: 1 | boolean;
    /** Business owner: drafts + needs_more_information only (My Tray). */
    initiator_tray?: boolean | 1;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/vendor-initiations', { params });
    return response.data;
  },
  listPreOnboardingInProgress: async (params?: {
    stage?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/vendor-initiations/pre-onboarding/in-progress', { params });
    return response.data;
  },
  getPreOnboardingInProgress: async (vendorInitiationId: number) => {
    const response = await api.get(
      `/vendor-initiations/pre-onboarding/in-progress/${vendorInitiationId}`,
    );
    return response.data;
  },
  /** Annexures where the current user is a parallel approver (default: all rows; pending_only=1 for items awaiting your vote). */
  listPendingAnnexures: async (params?: {
    page?: number;
    limit?: number;
    pending_only?: 0 | 1 | boolean;
  }) => {
    const response = await api.get('/vendor-initiations/annexures/pending', { params });
    return response.data;
  },
  businessHeadsDropdown: async () => {
    const response = await api.get('/users/business-heads');
    return response.data;
  },
  vmsLegalTeamDropdown: async () => {
    const response = await api.get('/users/vms-legal');
    return response.data;
  },
  vmsDpoTeamDropdown: async () => {
    const response = await api.get('/users/vms-dpo');
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/vendor-initiations/${id}`);
    return response.data?.data ?? response.data;
  },
  uploadAnnexureRowAttachment: async (
    vendorInitiationId: number,
    code: string,
    rowId: string,
    file: File
  ): Promise<{
    url: string;
    originalName: string;
    size: number;
    uploadedAt: string;
    storedName: string;
  }> => {
    const fd = new FormData();
    fd.append('rowId', rowId);
    fd.append('file', file);
    const response = await api.post(
      `/vendor-initiations/${vendorInitiationId}/annexures/${encodeURIComponent(code)}/row-attachment`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const data = response.data?.data ?? response.data;
    return data as {
      url: string;
      originalName: string;
      size: number;
      uploadedAt: string;
      storedName: string;
    };
  },
  listAnnexureRowComments: async (id: number, code: string): Promise<AnnexureRowApproverComment[]> => {
    const response = await api.get(`/vendor-initiations/${id}/annexures/${encodeURIComponent(code)}/row-comments`);
    return ((response.data?.data ?? []) as AnnexureRowApproverComment[]).map((r) => ({
      id: Number(r.id),
      row_id: String(r.row_id),
      approver_id: Number(r.approver_id),
      comment: String(r.comment ?? ''),
      updated_at: r.updated_at == null ? null : String(r.updated_at),
    }));
  },
  upsertAnnexureRowComment: async (id: number, code: string, body: { rowId: string; comment: string }) => {
    const response = await api.put(`/vendor-initiations/${id}/annexures/${encodeURIComponent(code)}/row-comments`, body);
    return response.data;
  },
  getAuditTrail: async (id: number) => {
    const response = await api.get(`/vendor-initiations/${id}/audit-trail`);
    return response.data?.data ?? response.data;
  },
  create: async (formData: FormData) => {
    const response = await api.post('/vendor-initiations', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.patch(`/vendor-initiations/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  patchAnnexureApprovers: async (id: number, code: string, body: { approver_ids: number[] }) => {
    const response = await api.patch(
      `/vendor-initiations/${id}/annexures/${encodeURIComponent(code)}/approvers`,
      body
    );
    return response.data;
  },
  approve: async (
    id: number,
    body: { action: 'approve'; notes?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    if (files.length === 0) {
      const response = await api.post(`/vendor-initiations/${id}/approve`, {
        action: 'approve',
        notes: body.notes,
      });
      return response.data;
    }
    const fd = new FormData();
    fd.append('action', 'approve');
    if (body.notes?.trim()) fd.append('notes', body.notes.trim());
    for (const f of files) {
      fd.append('approver_attachments', f);
    }
    const response = await api.post(`/vendor-initiations/${id}/approve`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  reject: async (id: number, body: { action: 'reject'; notes?: string }) => {
    const response = await api.post(`/vendor-initiations/${id}/reject`, body);
    return response.data;
  },
  requestNeedMoreInformation: async (id: number, body: { comment: string }) => {
    const response = await api.post(`/vendor-initiations/${id}/need-more-information`, body);
    return response.data;
  },
  approveAnnexure: async (
    id: number,
    code: string,
    options?: { approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = options?.approverAttachments ?? [];
    const note = options?.approverComment?.trim() ?? '';
    if (files.length === 0 && !note) {
      const response = await api.post(`/vendor-initiations/${id}/annexures/${code}/approve`, {});
      return response.data;
    }
    const fd = new FormData();
    appendAnnexureApproverExtras(fd, options);
    const response = await api.post(`/vendor-initiations/${id}/annexures/${code}/approve`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  rejectAnnexure: async (id: number, code: string, body?: { notes?: string }) => {
    const response = await api.post(`/vendor-initiations/${id}/annexures/${code}/reject`, body ?? {});
    return response.data;
  },
  needMoreInformationAnnexure: async (
    id: number,
    code: string,
    body: { comment: string; approverComment?: string; approverAttachments?: File[] }
  ) => {
    const files = body.approverAttachments ?? [];
    const extraNote = body.approverComment?.trim() ?? '';
    if (files.length > 0 || extraNote) {
      const fd = new FormData();
      fd.append('comment', body.comment);
      appendAnnexureApproverExtras(fd, {
        approverComment: body.approverComment,
        approverAttachments: files,
      });
      const response = await api.post(
        `/vendor-initiations/${id}/annexures/${encodeURIComponent(code)}/need-more-information`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
    const response = await api.post(
      `/vendor-initiations/${id}/annexures/${encodeURIComponent(code)}/need-more-information`,
      { comment: body.comment }
    );
    return response.data;
  },
  delete: async (id: number, deletion_reason?: string) => {
    const response = await api.delete(`/vendor-initiations/${id}`, {
      data: deletion_reason?.trim() ? { deletion_reason: deletion_reason.trim() } : undefined,
    });
    return response.data;
  },
  listMyAssignedApproverInitiations: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/vendor-initiations/approver/my-initiations', { params });
    return response.data;
  },
  listMyStakeholderInitiations: async (params?: { page?: number; limit?: number }) => {
    const response = await api.get('/vendor-initiations/stakeholder/my-initiations', { params });
    return response.data;
  },
  getMyStakeholderAssignment: async (
    vendorInitiationId: number,
  ): Promise<{ success?: boolean; data?: { assigned?: boolean; has_stakeholder_role?: boolean } }> => {
    const response = await api.get(`/vendor-initiations/${vendorInitiationId}/stakeholder/me`);
    return response.data;
  },
  getFlowStakeholders: async (id: number) => {
    const response = await api.get(`/vendor-initiations/${id}/stakeholders`);
    return response.data;
  },
  putFlowStakeholders: async (id: number, body: { user_ids: number[] }) => {
    const response = await api.put(`/vendor-initiations/${id}/stakeholders`, body);
    return response.data;
  },
  /** Active picklists for vendor initiation create/edit (service type, governance, SLA). */
  getReferenceOptions: async (): Promise<{
    service_types: { value: string; label: string }[];
    governance_approvals: { value: string; label: string }[];
    sla_types: { value: string; label: string }[];
    contract_types: { value: string; label: string }[];
  }> => {
    const response = await api.get('/vendor-initiations/reference-options');
    return (response.data?.data ?? response.data) as {
      service_types: { value: string; label: string }[];
      governance_approvals: { value: string; label: string }[];
      sla_types: { value: string; label: string }[];
      contract_types: { value: string; label: string }[];
    };
  },
};

export type VmsVendorInitiationListOptionRow = {
  id: number;
  category: string;
  option_value: string;
  label: string;
  sort_order: number;
  is_active: number | boolean;
};

export type AnnexureRowApproverComment = {
  id: number;
  row_id: string;
  approver_id: number;
  comment: string;
  updated_at: string | null;
};

export type VmsAdminVendorInitiationOptionSegment =
  | 'service-types'
  | 'governance-approvals'
  | 'sla-types'
  | 'contract-types';

/** VMS admin only — CRUD for vendor initiation dropdown / multi-select values. */
export const vmsAdminVendorInitiationOptionsAPI = {
  list: async (segment: VmsAdminVendorInitiationOptionSegment) => {
    const response = await api.get(`/vms/admin/vendor-initiation-options/${segment}`);
    return (response.data?.data ?? []) as VmsVendorInitiationListOptionRow[];
  },
  create: async (
    segment: VmsAdminVendorInitiationOptionSegment,
    body: { option_value: string; label: string; sort_order?: number; is_active?: boolean }
  ) => {
    const response = await api.post(`/vms/admin/vendor-initiation-options/${segment}`, body);
    return response.data as { success?: boolean; data?: { id: number }; message?: string };
  },
  update: async (
    segment: VmsAdminVendorInitiationOptionSegment,
    id: number,
    body: Partial<{ option_value: string; label: string; sort_order: number; is_active: boolean }>
  ) => {
    const response = await api.patch(`/vms/admin/vendor-initiation-options/${segment}/${id}`, body);
    return response.data as { success?: boolean; message?: string };
  },
  delete: async (segment: VmsAdminVendorInitiationOptionSegment, id: number) => {
    const response = await api.delete(`/vms/admin/vendor-initiation-options/${segment}/${id}`);
    return response.data as { success?: boolean; message?: string };
  },
};

export default api;
